// src/path-target.ts
function extractPathTarget(text) {
  const trimmed = text.trimStart();
  const match = /^:([A-Za-z_]\w*(?:\.[A-Za-z_]\w*)*)(?:\s+([\s\S]*))?$/u.exec(trimmed);
  if (!match?.[1]) {
    return null;
  }
  return {
    path: { segments: match[1].split(".") },
    remainingText: match[2] ?? ""
  };
}
function formatPathTarget(path2) {
  if ("segments" in path2) {
    return path2.segments.join(".");
  }
  return path2.join(".");
}

// src/tag-registry.ts
import {
  BUILTIN_CONSTRAINT_DEFINITIONS,
  normalizeConstraintTagName
} from "@formspec/core/internals";
var FORM_SPEC_PLACEMENTS = [
  "class",
  "class-field",
  "class-method",
  "interface",
  "interface-field",
  "type-alias",
  "type-alias-field",
  "variable",
  "function",
  "function-parameter",
  "method-parameter"
];
var FORM_SPEC_TARGET_KINDS = [
  "none",
  "path",
  "member",
  "variant"
];
var FIELD_PLACEMENTS = [
  "class-field",
  "interface-field",
  "type-alias-field",
  "variable",
  "function-parameter",
  "method-parameter"
];
var TYPE_PLACEMENTS = [
  "class",
  "interface",
  "type-alias"
];
var DECLARATION_PLACEMENTS = [
  "class-method",
  "function"
];
var ALL_PLACEMENTS = [
  ...TYPE_PLACEMENTS,
  ...FIELD_PLACEMENTS,
  ...DECLARATION_PLACEMENTS
];
var INTEGER_VALUE_TAGS = /* @__PURE__ */ new Set(["minLength", "maxLength", "minItems", "maxItems"]);
var SIGNED_INTEGER_VALUE_TAGS = /* @__PURE__ */ new Set(["order"]);
var JSON_VALUE_TAGS = /* @__PURE__ */ new Set(["const", "enumOptions"]);
var BOOLEAN_VALUE_TAGS = /* @__PURE__ */ new Set(["uniqueItems"]);
var STRING_VALUE_TAGS = /* @__PURE__ */ new Set([
  "pattern",
  "displayName",
  "description",
  "format",
  "placeholder",
  "group",
  "example",
  "remarks",
  "see",
  "apiName"
]);
var CONDITION_VALUE_TAGS = /* @__PURE__ */ new Set(["showWhen", "hideWhen", "enableWhen", "disableWhen"]);
var CONSTRAINT_COMPLETION_DETAIL = {
  minimum: "Minimum numeric value (inclusive). Example: `@minimum 0`",
  maximum: "Maximum numeric value (inclusive). Example: `@maximum 100`",
  exclusiveMinimum: "Minimum numeric value (exclusive). Example: `@exclusiveMinimum 0`",
  exclusiveMaximum: "Maximum numeric value (exclusive). Example: `@exclusiveMaximum 100`",
  multipleOf: "Value must be a multiple of this number. Example: `@multipleOf 0.01`",
  minLength: "Minimum string length. Example: `@minLength 1`",
  maxLength: "Maximum string length. Example: `@maxLength 255`",
  minItems: "Minimum number of array items. Example: `@minItems 1`",
  maxItems: "Maximum number of array items. Example: `@maxItems 10`",
  uniqueItems: "Require all array items to be distinct. Example: `@uniqueItems`",
  pattern: "Regular expression pattern for string validation. Example: `@pattern ^[a-z]+$`",
  enumOptions: 'Inline JSON array of allowed enum values. Example: `@enumOptions ["a","b","c"]`',
  const: 'Require a constant JSON value. Example: `@const "USD"`'
};
var CONSTRAINT_HOVER_DOCS = {
  minimum: [
    "**@minimum** `<number>`",
    "",
    "Sets an inclusive lower bound on a numeric field.",
    "",
    "Maps to `minimum` in JSON Schema.",
    "",
    "**Signature:** `@minimum [:path] <number>`"
  ].join("\n"),
  maximum: [
    "**@maximum** `<number>`",
    "",
    "Sets an inclusive upper bound on a numeric field.",
    "",
    "Maps to `maximum` in JSON Schema.",
    "",
    "**Signature:** `@maximum [:path] <number>`"
  ].join("\n"),
  exclusiveMinimum: [
    "**@exclusiveMinimum** `<number>`",
    "",
    "Sets an exclusive lower bound on a numeric field.",
    "",
    "Maps to `exclusiveMinimum` in JSON Schema.",
    "",
    "**Signature:** `@exclusiveMinimum [:path] <number>`"
  ].join("\n"),
  exclusiveMaximum: [
    "**@exclusiveMaximum** `<number>`",
    "",
    "Sets an exclusive upper bound on a numeric field.",
    "",
    "Maps to `exclusiveMaximum` in JSON Schema.",
    "",
    "**Signature:** `@exclusiveMaximum [:path] <number>`"
  ].join("\n"),
  multipleOf: [
    "**@multipleOf** `<number>`",
    "",
    "Requires the numeric value to be a multiple of the given number.",
    "",
    "Maps to `multipleOf` in JSON Schema.",
    "",
    "**Signature:** `@multipleOf [:path] <number>`"
  ].join("\n"),
  minLength: [
    "**@minLength** `<integer>`",
    "",
    "Sets a minimum character length on a string field.",
    "",
    "Maps to `minLength` in JSON Schema.",
    "",
    "**Signature:** `@minLength [:path] <integer>`"
  ].join("\n"),
  maxLength: [
    "**@maxLength** `<integer>`",
    "",
    "Sets a maximum character length on a string field.",
    "",
    "Maps to `maxLength` in JSON Schema.",
    "",
    "**Signature:** `@maxLength [:path] <integer>`"
  ].join("\n"),
  minItems: [
    "**@minItems** `<integer>`",
    "",
    "Sets a minimum number of items in an array field.",
    "",
    "Maps to `minItems` in JSON Schema.",
    "",
    "**Signature:** `@minItems [:path] <integer>`"
  ].join("\n"),
  maxItems: [
    "**@maxItems** `<integer>`",
    "",
    "Sets a maximum number of items in an array field.",
    "",
    "Maps to `maxItems` in JSON Schema.",
    "",
    "**Signature:** `@maxItems [:path] <integer>`"
  ].join("\n"),
  uniqueItems: [
    "**@uniqueItems**",
    "",
    "Requires all items in an array field to be distinct.",
    "",
    "Maps to `uniqueItems` in JSON Schema.",
    "",
    "**Signature:** `@uniqueItems [:path]`"
  ].join("\n"),
  pattern: [
    "**@pattern** `<regex>`",
    "",
    "Sets a regular expression pattern that a string field must match.",
    "",
    "Maps to `pattern` in JSON Schema.",
    "",
    "**Signature:** `@pattern [:path] <regex>`"
  ].join("\n"),
  enumOptions: [
    "**@enumOptions** `<json-array>`",
    "",
    "Specifies the allowed values for an enum field as an inline JSON array.",
    "",
    "Maps to `enum` in JSON Schema.",
    "",
    "**Signature:** `@enumOptions <json-array>`"
  ].join("\n"),
  const: [
    "**@const** `<json-literal>`",
    "",
    "Requires the field value to equal a single constant JSON value.",
    "",
    "Maps to `const` in JSON Schema.",
    "",
    "**Signature:** `@const [:path] <json-literal>`"
  ].join("\n")
};
var CONSTRAINT_HOVER_SUMMARIES = {
  minimum: "Sets an inclusive lower bound on a numeric field.",
  maximum: "Sets an inclusive upper bound on a numeric field.",
  exclusiveMinimum: "Sets an exclusive lower bound on a numeric field.",
  exclusiveMaximum: "Sets an exclusive upper bound on a numeric field.",
  multipleOf: "Requires the numeric value to be a multiple of the given number.",
  minLength: "Sets a minimum character length on a string field.",
  maxLength: "Sets a maximum character length on a string field.",
  minItems: "Sets a minimum number of items in an array field.",
  maxItems: "Sets a maximum number of items in an array field.",
  uniqueItems: "Requires all items in an array field to be distinct.",
  pattern: "Sets a regular expression pattern that a string field must match.",
  enumOptions: "Specifies the allowed values for an enum field as an inline JSON array.",
  const: "Requires the field value to equal a single constant JSON value."
};
function inferValueKind(name) {
  if (INTEGER_VALUE_TAGS.has(name)) return "integer";
  if (SIGNED_INTEGER_VALUE_TAGS.has(name)) return "signedInteger";
  if (JSON_VALUE_TAGS.has(name)) return "json";
  if (BOOLEAN_VALUE_TAGS.has(name)) return "boolean";
  if (STRING_VALUE_TAGS.has(name)) return "string";
  if (CONDITION_VALUE_TAGS.has(name)) return "condition";
  return null;
}
function getBuiltinValueKind(name) {
  return inferValueKind(name) ?? "number";
}
function getBuiltinConstraintCapability(name) {
  switch (name) {
    case "minimum":
    case "maximum":
    case "exclusiveMinimum":
    case "exclusiveMaximum":
    case "multipleOf":
      return "numeric-comparable";
    case "minLength":
    case "maxLength":
    case "pattern":
      return "string-like";
    case "minItems":
    case "maxItems":
    case "uniqueItems":
      return "array-like";
    case "enumOptions":
      return "enum-member-addressable";
    case "const":
      return "json-like";
    default: {
      const exhaustive = name;
      return exhaustive;
    }
  }
}
function capabilitiesForValueKind(valueKind) {
  switch (valueKind) {
    case "number":
    case "integer":
    case "signedInteger":
      return ["numeric-comparable"];
    case "string":
      return ["string-like"];
    case "json":
      return ["json-like"];
    case "condition":
      return ["condition-like"];
    case "boolean":
    case null:
      return [];
    default: {
      const exhaustive = valueKind;
      return exhaustive;
    }
  }
}
function valueLabelForKind(valueKind, fallback = "<value>") {
  switch (valueKind) {
    case "number":
      return "<number>";
    case "integer":
    case "signedInteger":
      return "<integer>";
    case "string":
      return "<text>";
    case "json":
      return "<json>";
    case "condition":
      return "<condition>";
    case "boolean":
    case null:
      return "";
    default: {
      const exhaustive = valueKind;
      void exhaustive;
      return fallback;
    }
  }
}
function targetLabelForKind(kind) {
  switch (kind) {
    case "path":
      return "[:path]";
    case "member":
      return ":member";
    case "variant":
      return ":variant";
    default: {
      const exhaustive = kind;
      return exhaustive;
    }
  }
}
function parameterKindForTarget(targetKind) {
  switch (targetKind) {
    case "path":
      return "target-path";
    case "member":
      return "target-member";
    case "variant":
      return "target-variant";
    default: {
      const exhaustive = targetKind;
      return exhaustive;
    }
  }
}
function createTargetParameter(targetKind, valueKind, pathCapability) {
  const base = {
    kind: parameterKindForTarget(targetKind),
    label: targetLabelForKind(targetKind),
    optional: targetKind === "path"
  };
  if (targetKind === "path") {
    const capability = pathCapability ?? capabilitiesForValueKind(valueKind)[0];
    return capability === void 0 ? base : { ...base, capability };
  }
  if (targetKind === "member") {
    return { ...base, capability: "enum-member-addressable" };
  }
  return base;
}
function createSignature(name, placements, targetKind, valueKind, valueLabel, pathCapability) {
  const parameters = [];
  if (targetKind !== null) {
    parameters.push(createTargetParameter(targetKind, valueKind, pathCapability));
  }
  if (valueLabel !== "") {
    parameters.push(
      valueKind === null ? {
        kind: "value",
        label: valueLabel
      } : {
        kind: "value",
        label: valueLabel,
        valueKind
      }
    );
  }
  const targetLabel = targetKind === null ? "" : ` ${targetLabelForKind(targetKind)}`;
  const valueLabelSuffix = valueLabel === "" ? "" : ` ${valueLabel}`;
  return {
    label: `@${name}${targetLabel}${valueLabelSuffix}`,
    placements,
    parameters
  };
}
function buildHoverMarkdown(name, hoverSummary, signatures, valueLabel) {
  const header = valueLabel === "" ? `**@${name}**` : `**@${name}** \`${valueLabel}\``;
  const signatureLines = signatures.length === 1 ? [`**Signature:** \`${signatures[0]?.label ?? `@${name}`}\``] : ["**Signatures:**", ...signatures.map((signature) => `- \`${signature.label}\``)];
  return [header, "", hoverSummary, "", ...signatureLines].join("\n");
}
function makeConstraintSignatures(name) {
  const valueKind = getBuiltinValueKind(name);
  const subjectCapability = getBuiltinConstraintCapability(name);
  const valueLabel = name === "pattern" ? "<regex>" : name === "enumOptions" ? "<json-array>" : name === "const" ? "<json-literal>" : valueLabelForKind(valueKind);
  return [
    createSignature(name, FIELD_PLACEMENTS, null, valueKind, valueLabel),
    createSignature(name, FIELD_PLACEMENTS, "path", valueKind, valueLabel, subjectCapability)
  ];
}
var BUILTIN_TAG_DEFINITIONS = Object.fromEntries(
  Object.keys(BUILTIN_CONSTRAINT_DEFINITIONS).map((name) => {
    const valueKind = getBuiltinValueKind(name);
    const subjectCapability = getBuiltinConstraintCapability(name);
    return [
      name,
      {
        canonicalName: name,
        valueKind,
        requiresArgument: valueKind !== "boolean",
        supportedTargets: ["none", "path"],
        allowDuplicates: false,
        category: "constraint",
        placements: FIELD_PLACEMENTS,
        capabilities: [subjectCapability],
        completionDetail: CONSTRAINT_COMPLETION_DETAIL[name] ?? `@${name}`,
        hoverSummary: CONSTRAINT_HOVER_SUMMARIES[name],
        hoverMarkdown: CONSTRAINT_HOVER_DOCS[name] ?? `**@${name}**`,
        signatures: makeConstraintSignatures(name)
      }
    ];
  })
);
var EXTRA_TAG_SPECS = {
  displayName: {
    requiresArgument: true,
    supportedTargets: ["none", "member", "variant"],
    allowDuplicates: false,
    category: "annotation",
    placements: [...TYPE_PLACEMENTS, ...FIELD_PLACEMENTS],
    completionDetail: "Display label for a type, field, or enum member.",
    hoverSummary: "Provides a user-facing display label.",
    valueLabel: "<label>",
    targetPlacements: {
      member: FIELD_PLACEMENTS,
      variant: TYPE_PLACEMENTS
    }
  },
  description: {
    requiresArgument: true,
    supportedTargets: ["none"],
    allowDuplicates: false,
    category: "annotation",
    placements: [...TYPE_PLACEMENTS, ...FIELD_PLACEMENTS],
    completionDetail: "Description text for a type or field.",
    hoverSummary: "Provides descriptive documentation for a type or field."
  },
  format: {
    requiresArgument: true,
    supportedTargets: ["none"],
    allowDuplicates: false,
    category: "annotation",
    placements: FIELD_PLACEMENTS,
    completionDetail: "Format hint for a field.",
    hoverSummary: "Provides a format hint for a field.",
    valueLabel: "<format>"
  },
  placeholder: {
    requiresArgument: true,
    supportedTargets: ["none"],
    allowDuplicates: false,
    category: "annotation",
    placements: FIELD_PLACEMENTS,
    completionDetail: "Placeholder text for a field.",
    hoverSummary: "Provides placeholder text for a field."
  },
  order: {
    requiresArgument: true,
    supportedTargets: ["none"],
    allowDuplicates: false,
    category: "annotation",
    placements: FIELD_PLACEMENTS,
    completionDetail: "Field display order hint.",
    hoverSummary: "Provides an integer ordering hint for UI layout."
  },
  apiName: {
    requiresArgument: true,
    supportedTargets: ["none", "member", "variant"],
    allowDuplicates: false,
    category: "annotation",
    placements: [...TYPE_PLACEMENTS, ...FIELD_PLACEMENTS],
    completionDetail: "API-facing serialized name for a type, field, or variant.",
    hoverSummary: "Overrides the serialized API name used in generated schema output.",
    valueLabel: "<identifier>",
    targetPlacements: {
      member: FIELD_PLACEMENTS,
      variant: TYPE_PLACEMENTS
    }
  },
  group: {
    requiresArgument: true,
    supportedTargets: ["none"],
    allowDuplicates: false,
    category: "structure",
    placements: FIELD_PLACEMENTS,
    completionDetail: "Assigns a field to a UI group.",
    hoverSummary: "Assigns the field to a named grouping container.",
    valueLabel: "<group>"
  },
  showWhen: {
    requiresArgument: true,
    supportedTargets: ["none"],
    allowDuplicates: true,
    category: "structure",
    placements: FIELD_PLACEMENTS,
    completionDetail: "Conditional visibility rule.",
    hoverSummary: "Shows the field only when the condition is satisfied."
  },
  hideWhen: {
    requiresArgument: true,
    supportedTargets: ["none"],
    allowDuplicates: true,
    category: "structure",
    placements: FIELD_PLACEMENTS,
    completionDetail: "Conditional visibility suppression rule.",
    hoverSummary: "Hides the field when the condition is satisfied."
  },
  enableWhen: {
    requiresArgument: true,
    supportedTargets: ["none"],
    allowDuplicates: true,
    category: "structure",
    placements: FIELD_PLACEMENTS,
    completionDetail: "Conditional interactivity rule.",
    hoverSummary: "Enables the field only when the condition is satisfied."
  },
  disableWhen: {
    requiresArgument: true,
    supportedTargets: ["none"],
    allowDuplicates: true,
    category: "structure",
    placements: FIELD_PLACEMENTS,
    completionDetail: "Conditional disablement rule.",
    hoverSummary: "Disables the field when the condition is satisfied."
  },
  defaultValue: {
    requiresArgument: true,
    supportedTargets: ["none"],
    allowDuplicates: false,
    category: "ecosystem",
    placements: FIELD_PLACEMENTS,
    completionDetail: "Default JSON value for a field.",
    hoverSummary: "Provides a default JSON value for ecosystem integrations.",
    valueLabel: "<value>"
  },
  deprecated: {
    requiresArgument: false,
    supportedTargets: ["none"],
    allowDuplicates: false,
    category: "ecosystem",
    placements: ALL_PLACEMENTS,
    completionDetail: "Marks a declaration as deprecated.",
    hoverSummary: "Marks the declaration as deprecated."
  },
  example: {
    requiresArgument: true,
    supportedTargets: ["none"],
    allowDuplicates: true,
    category: "ecosystem",
    placements: [...TYPE_PLACEMENTS, ...FIELD_PLACEMENTS],
    completionDetail: "Example serialized value.",
    hoverSummary: "Provides an example value for documentation and tooling."
  },
  discriminator: {
    requiresArgument: true,
    supportedTargets: ["path"],
    allowDuplicates: false,
    category: "annotation",
    placements: TYPE_PLACEMENTS,
    completionDetail: "Declare a discriminator field for a generic object type.",
    hoverSummary: "Declares the field used as the discriminator for a generic object type.",
    valueLabel: "<typeParam>"
  },
  remarks: {
    requiresArgument: true,
    supportedTargets: ["none"],
    allowDuplicates: false,
    category: "ecosystem",
    placements: ALL_PLACEMENTS,
    completionDetail: "Additional remarks text.",
    hoverSummary: "Provides additional remarks for the declaration."
  },
  see: {
    requiresArgument: true,
    supportedTargets: ["none"],
    allowDuplicates: true,
    category: "ecosystem",
    placements: ALL_PLACEMENTS,
    completionDetail: "Reference to related documentation.",
    hoverSummary: "References related documentation or declarations.",
    valueLabel: "<reference>"
  }
};
function buildExtraTagDefinition(canonicalName, spec) {
  const valueKind = spec.valueKind ?? inferValueKind(canonicalName);
  const valueLabel = spec.requiresArgument ? spec.valueLabel ?? valueLabelForKind(valueKind) : "";
  const signatures = [];
  if (spec.supportedTargets.includes("none")) {
    signatures.push(createSignature(canonicalName, spec.placements, null, valueKind, valueLabel));
  }
  if (spec.supportedTargets.includes("path")) {
    signatures.push(
      createSignature(
        canonicalName,
        spec.targetPlacements?.path ?? spec.placements,
        "path",
        valueKind,
        valueLabel
      )
    );
  }
  if (spec.supportedTargets.includes("member")) {
    signatures.push(
      createSignature(
        canonicalName,
        spec.targetPlacements?.member ?? spec.placements,
        "member",
        valueKind,
        valueLabel
      )
    );
  }
  if (spec.supportedTargets.includes("variant")) {
    signatures.push(
      createSignature(
        canonicalName,
        spec.targetPlacements?.variant ?? spec.placements,
        "variant",
        valueKind,
        valueLabel
      )
    );
  }
  return {
    canonicalName,
    valueKind,
    requiresArgument: spec.requiresArgument,
    supportedTargets: spec.supportedTargets,
    allowDuplicates: spec.allowDuplicates,
    category: spec.category,
    placements: spec.placements,
    capabilities: capabilitiesForValueKind(valueKind),
    completionDetail: spec.completionDetail,
    hoverSummary: spec.hoverSummary,
    hoverMarkdown: buildHoverMarkdown(canonicalName, spec.hoverSummary, signatures, valueLabel),
    signatures
  };
}
function placementsForMetadataDeclarationKinds(declarationKinds) {
  const placements = /* @__PURE__ */ new Set();
  for (const declarationKind of declarationKinds) {
    switch (declarationKind) {
      case "type":
        for (const placement of TYPE_PLACEMENTS) {
          placements.add(placement);
        }
        break;
      case "field":
        for (const placement of FIELD_PLACEMENTS) {
          placements.add(placement);
        }
        break;
      case "method":
        placements.add("class-method");
        placements.add("function");
        break;
      default: {
        const _exhaustive = declarationKind;
        return _exhaustive;
      }
    }
  }
  return [...placements];
}
function buildExtensionMetadataTagDefinition(extensionId, slot) {
  const canonicalName = normalizeFormSpecTagName(slot.tagName);
  const supportsQualifiers = (slot.qualifiers?.length ?? 0) > 0;
  if (slot.allowBare === false && !supportsQualifiers) {
    throw new Error(
      `Metadata tag "@${canonicalName}" must allow bare usage or declare at least one qualifier.`
    );
  }
  const supportedTargets = supportsQualifiers ? slot.allowBare === false ? ["variant"] : ["none", "variant"] : slot.allowBare === false ? [] : ["none"];
  const placements = placementsForMetadataDeclarationKinds(slot.declarationKinds);
  const signatures = [];
  const valueKind = "string";
  if (supportedTargets.includes("none")) {
    signatures.push(createSignature(canonicalName, placements, null, valueKind, "<value>"));
  }
  if (supportedTargets.includes("variant")) {
    signatures.push(createSignature(canonicalName, placements, "variant", valueKind, "<value>"));
  }
  return {
    canonicalName,
    valueKind,
    requiresArgument: true,
    supportedTargets,
    allowDuplicates: false,
    category: "annotation",
    placements,
    capabilities: capabilitiesForValueKind(valueKind),
    completionDetail: `Extension metadata tag from ${extensionId}`,
    hoverSummary: `Extension-defined metadata tag from \`${extensionId}\`.`,
    hoverMarkdown: [
      `**@${canonicalName}** \`<value>\``,
      "",
      `Extension-defined metadata tag from \`${extensionId}\`.`,
      "",
      signatures.map((signature) => `**Signature:** \`${signature.label}\``).join("\n")
    ].join("\n"),
    signatures
  };
}
var EXTRA_TAG_DEFINITIONS = Object.fromEntries(
  Object.entries(EXTRA_TAG_SPECS).map(([canonicalName, spec]) => [
    canonicalName,
    buildExtraTagDefinition(canonicalName, spec)
  ])
);
function normalizeFormSpecTagName(rawName) {
  return normalizeConstraintTagName(rawName);
}
function getTagDefinition(rawName, extensions) {
  const normalized = normalizeFormSpecTagName(rawName);
  const builtin = BUILTIN_TAG_DEFINITIONS[normalized];
  if (builtin !== void 0) {
    return builtin;
  }
  const extra = EXTRA_TAG_DEFINITIONS[normalized];
  if (extra !== void 0) {
    return extra;
  }
  const extensionRegistration = getExtensionConstraintTags(extensions).find(
    (tag) => tag.tagName === normalized
  );
  if (extensionRegistration !== void 0) {
    return {
      canonicalName: extensionRegistration.tagName,
      valueKind: null,
      requiresArgument: true,
      supportedTargets: ["none"],
      allowDuplicates: true,
      category: "constraint",
      placements: FIELD_PLACEMENTS,
      capabilities: [],
      completionDetail: `Extension constraint tag from ${extensionRegistration.extensionId}`,
      hoverSummary: `Extension-defined constraint tag from \`${extensionRegistration.extensionId}\`.`,
      hoverMarkdown: [
        `**@${extensionRegistration.tagName}** \`<value>\``,
        "",
        `Extension-defined constraint tag from \`${extensionRegistration.extensionId}\`.`,
        "",
        `**Signature:** \`@${extensionRegistration.tagName} <value>\``
      ].join("\n"),
      signatures: [
        {
          label: `@${extensionRegistration.tagName} <value>`,
          placements: FIELD_PLACEMENTS,
          parameters: [{ kind: "value", label: "<value>" }]
        }
      ]
    };
  }
  const extensionMetadata = getExtensionMetadataSlots(extensions).find(
    (slot) => slot.tagName === normalized
  );
  return extensionMetadata === void 0 ? null : buildExtensionMetadataTagDefinition(extensionMetadata.extensionId, extensionMetadata.slot);
}
function getConstraintTagDefinitions(extensions) {
  const builtins = Object.values(BUILTIN_TAG_DEFINITIONS);
  const custom = getExtensionConstraintTags(extensions).map((tag) => getTagDefinition(tag.tagName, extensions)).filter((tag) => tag !== null);
  return [...builtins, ...custom];
}
function getAllTagDefinitions(extensions) {
  const builtins = Object.values(BUILTIN_TAG_DEFINITIONS);
  const extras = Object.values(EXTRA_TAG_DEFINITIONS);
  const custom = getExtensionConstraintTags(extensions).map((tag) => getTagDefinition(tag.tagName, extensions)).filter((tag) => tag !== null);
  const customMetadata = getExtensionMetadataSlots(extensions).map((slot) => getTagDefinition(slot.tagName, extensions)).filter((tag) => tag !== null);
  return [...builtins, ...extras, ...custom, ...customMetadata];
}
function getTagHoverMarkdown(rawName, extensions) {
  return getTagDefinition(rawName, extensions)?.hoverMarkdown ?? null;
}
function getExtensionConstraintTags(extensions) {
  return extensions?.flatMap((extension) => {
    const tagRecords = extension.constraintTags ?? [];
    return tagRecords.map((tag) => ({
      extensionId: extension.extensionId,
      tagName: normalizeFormSpecTagName(tag.tagName)
    }));
  }) ?? [];
}
function getExtensionMetadataSlots(extensions) {
  return extensions?.flatMap(
    (extension) => (extension.metadataSlots ?? []).map((slot) => ({
      extensionId: extension.extensionId,
      tagName: normalizeFormSpecTagName(slot.tagName),
      slot
    }))
  ) ?? [];
}

// src/comment-syntax.ts
function isWhitespace(char) {
  return char === " " || char === "	" || char === "\n" || char === "\r";
}
function isTagStart(lineText, index) {
  if (lineText[index] !== "@") {
    return false;
  }
  const nextChar = lineText[index + 1];
  if (nextChar === void 0 || !/[A-Za-z]/u.test(nextChar)) {
    return false;
  }
  const previousChar = lineText[index - 1];
  return previousChar === void 0 || isWhitespace(previousChar);
}
function collectTagStarts(lineText) {
  const tagStarts = [];
  for (let index = 0; index < lineText.length; index += 1) {
    if (isTagStart(lineText, index)) {
      tagStarts.push(index);
    }
  }
  return tagStarts;
}
function findTagEnd(lineText, index) {
  let cursor = index + 1;
  while (cursor < lineText.length && /[A-Za-z0-9]/u.test(lineText[cursor] ?? "")) {
    cursor += 1;
  }
  return cursor;
}
function trimTrailingWhitespace(lineText, end) {
  let cursor = end;
  while (cursor > 0 && isWhitespace(lineText[cursor - 1])) {
    cursor -= 1;
  }
  return cursor;
}
function spanFromLine(line, start, end, baseOffset) {
  const rawStart = line.rawOffsets[start];
  if (rawStart === void 0) {
    throw new Error(`Invalid projected span start: ${String(start)}`);
  }
  const rawEnd = end >= line.text.length ? line.rawContentEnd : (line.rawOffsets[end - 1] ?? line.rawContentEnd - 1) + 1;
  return {
    start: baseOffset + rawStart,
    end: baseOffset + rawEnd
  };
}
function classifyTargetKind(canonicalName, targetText, extensions) {
  if (targetText === "singular" || targetText === "plural") {
    return "variant";
  }
  if (targetText.includes(".")) {
    return "path";
  }
  const definition = getTagDefinition(canonicalName, extensions);
  const supportedTargets = definition?.supportedTargets.filter((target) => target !== "none") ?? [];
  if (supportedTargets.includes("path")) {
    return "path";
  }
  if (supportedTargets.includes("member") && supportedTargets.includes("variant")) {
    return "ambiguous";
  }
  if (supportedTargets.includes("member")) {
    return "member";
  }
  if (supportedTargets.includes("variant")) {
    return "variant";
  }
  return "path";
}
function parseTargetSpecifier(line, payloadStart, payloadEnd, canonicalName, baseOffset, extensions) {
  if (payloadStart >= payloadEnd || line.text[payloadStart] !== ":") {
    return null;
  }
  let targetEnd = payloadStart + 1;
  while (targetEnd < payloadEnd && !isWhitespace(line.text[targetEnd])) {
    targetEnd += 1;
  }
  const fullText = line.text.slice(payloadStart, targetEnd);
  const targetText = fullText.slice(1);
  const parsedPath = extractPathTarget(fullText);
  const specifierSpan = spanFromLine(line, payloadStart + 1, targetEnd, baseOffset);
  return {
    rawText: targetText,
    valid: parsedPath !== null && parsedPath.remainingText === "",
    kind: classifyTargetKind(canonicalName, targetText, extensions),
    fullSpan: spanFromLine(line, payloadStart, targetEnd, baseOffset),
    colonSpan: spanFromLine(line, payloadStart, payloadStart + 1, baseOffset),
    span: specifierSpan,
    path: parsedPath?.path ?? null,
    localEnd: targetEnd
  };
}
function projectCommentLines(commentText) {
  const projections = [];
  const commentBodyStart = commentText.startsWith("/**") ? 3 : commentText.startsWith("/*") ? 2 : 0;
  const commentBodyEnd = commentText.endsWith("*/") ? commentText.length - 2 : commentText.length;
  let cursor = commentBodyStart;
  while (cursor <= commentBodyEnd) {
    const lineStart = cursor;
    let lineEnd = cursor;
    while (lineEnd < commentBodyEnd && commentText[lineEnd] !== "\n") {
      lineEnd += 1;
    }
    let contentEnd = lineEnd;
    if (contentEnd > lineStart && commentText[contentEnd - 1] === "\r") {
      contentEnd -= 1;
    }
    let contentStart = lineStart;
    while (contentStart < contentEnd && (commentText[contentStart] === " " || commentText[contentStart] === "	")) {
      contentStart += 1;
    }
    if (contentStart < contentEnd && commentText[contentStart] === "*") {
      contentStart += 1;
      while (contentStart < contentEnd && (commentText[contentStart] === " " || commentText[contentStart] === "	")) {
        contentStart += 1;
      }
    }
    const rawOffsets = [];
    let text = "";
    for (let index = contentStart; index < contentEnd; index += 1) {
      rawOffsets.push(index);
      text += commentText[index] ?? "";
    }
    projections.push({
      text,
      rawOffsets,
      rawContentEnd: contentEnd
    });
    if (lineEnd >= commentBodyEnd) {
      break;
    }
    cursor = lineEnd + 1;
  }
  return projections;
}
function parseCommentBlock(commentText, options) {
  const tags = [];
  const baseOffset = options?.offset ?? 0;
  for (const line of projectCommentLines(commentText)) {
    const tagStarts = collectTagStarts(line.text);
    for (let tagIndex = 0; tagIndex < tagStarts.length; tagIndex += 1) {
      const tagStart = tagStarts[tagIndex];
      if (tagStart === void 0) {
        continue;
      }
      const tagEnd = findTagEnd(line.text, tagStart);
      const nextTagStart = tagStarts[tagIndex + 1] ?? line.text.length;
      const trimmedTagSegmentEnd = trimTrailingWhitespace(line.text, nextTagStart);
      const rawName = line.text.slice(tagStart + 1, tagEnd);
      const canonicalName = normalizeFormSpecTagName(rawName);
      let payloadStart = tagEnd;
      while (payloadStart < trimmedTagSegmentEnd && isWhitespace(line.text[payloadStart])) {
        payloadStart += 1;
      }
      const target = parseTargetSpecifier(
        line,
        payloadStart,
        trimmedTagSegmentEnd,
        canonicalName,
        baseOffset,
        options?.extensions
      );
      let valueStart = payloadStart;
      if (target !== null) {
        valueStart = target.localEnd;
        while (valueStart < trimmedTagSegmentEnd && isWhitespace(line.text[valueStart])) {
          valueStart += 1;
        }
      }
      const payloadSpan = payloadStart < trimmedTagSegmentEnd ? spanFromLine(line, payloadStart, trimmedTagSegmentEnd, baseOffset) : null;
      const valueSpan = valueStart < trimmedTagSegmentEnd ? spanFromLine(line, valueStart, trimmedTagSegmentEnd, baseOffset) : null;
      const parsedTarget = target === null ? null : {
        rawText: target.rawText,
        valid: target.valid,
        kind: target.kind,
        fullSpan: target.fullSpan,
        colonSpan: target.colonSpan,
        span: target.span,
        path: target.path
      };
      tags.push({
        rawTagName: rawName,
        normalizedTagName: canonicalName,
        recognized: getTagDefinition(canonicalName, options?.extensions) !== null,
        fullSpan: spanFromLine(line, tagStart, trimmedTagSegmentEnd, baseOffset),
        tagNameSpan: spanFromLine(line, tagStart, tagEnd, baseOffset),
        payloadSpan,
        colonSpan: parsedTarget?.colonSpan ?? null,
        target: parsedTarget,
        argumentSpan: valueSpan,
        argumentText: valueSpan === null ? "" : commentText.slice(valueSpan.start - baseOffset, valueSpan.end - baseOffset)
      });
    }
  }
  return {
    commentText,
    offset: baseOffset,
    tags
  };
}
function parseTagSyntax(rawTagName, payloadText, options) {
  const separator = payloadText === "" || isWhitespace(payloadText[0]) ? "" : " ";
  const parsed = parseCommentBlock(`/** @${rawTagName}${separator}${payloadText} */`, options);
  const [tag] = parsed.tags;
  if (tag === void 0) {
    throw new Error(`Unable to parse synthetic tag syntax for @${rawTagName}`);
  }
  return tag;
}
function extractCommentSummaryText(commentText) {
  const summaryLines = [];
  for (const line of projectCommentLines(commentText)) {
    let cutoff = line.text.length;
    for (let index = 0; index < line.text.length; index += 1) {
      if (isTagStart(line.text, index)) {
        cutoff = index;
        break;
      }
    }
    const segment = line.text.slice(0, cutoff).trimEnd();
    if (cutoff < line.text.length) {
      if (segment.trim() !== "") {
        summaryLines.push(segment.trim());
      }
      break;
    }
    summaryLines.push(segment);
  }
  return summaryLines.join("\n").trim();
}
function extractCommentBlockTagTexts(commentText, rawTagName) {
  const lines = projectCommentLines(commentText);
  const canonicalName = normalizeFormSpecTagName(rawTagName);
  const values = [];
  let lineIndex = 0;
  while (lineIndex < lines.length) {
    const line = lines[lineIndex];
    if (line === void 0) {
      break;
    }
    const tagStarts = collectTagStarts(line.text);
    let nextLineIndex = lineIndex + 1;
    for (let tagIndex = 0; tagIndex < tagStarts.length; tagIndex += 1) {
      const tagStart = tagStarts[tagIndex];
      if (tagStart === void 0) {
        continue;
      }
      const tagEnd = findTagEnd(line.text, tagStart);
      const candidateName = normalizeFormSpecTagName(line.text.slice(tagStart + 1, tagEnd));
      if (candidateName !== canonicalName) {
        continue;
      }
      let payloadStart = tagEnd;
      const nextTagStart = tagStarts[tagIndex + 1] ?? line.text.length;
      while (payloadStart < nextTagStart && isWhitespace(line.text[payloadStart])) {
        payloadStart += 1;
      }
      const blockLines = [
        line.text.slice(payloadStart, trimTrailingWhitespace(line.text, nextTagStart)).replace(/[ \t]+$/u, "")
      ];
      if (nextTagStart === line.text.length) {
        let continuationIndex = lineIndex + 1;
        while (continuationIndex < lines.length) {
          const continuation = lines[continuationIndex];
          if (continuation === void 0) {
            break;
          }
          if (collectTagStarts(continuation.text).length > 0) {
            break;
          }
          const continuationText = continuation.text.replace(/[ \t]+$/u, "");
          if (continuationText.trim() === "/") {
            break;
          }
          blockLines.push(continuationText);
          continuationIndex += 1;
        }
        if (continuationIndex > nextLineIndex) {
          nextLineIndex = continuationIndex;
        }
      }
      const text = blockLines.join("\n").trim();
      if (text !== "") {
        values.push(text);
      }
    }
    lineIndex = nextLineIndex;
  }
  return values;
}
function sliceCommentSpan(commentText, span, options) {
  const baseOffset = options?.offset ?? 0;
  return commentText.slice(span.start - baseOffset, span.end - baseOffset);
}

// src/ts-binding.ts
import * as ts from "typescript";
function stripNullishUnion(type) {
  if (!type.isUnion()) {
    return type;
  }
  const nonNullish = type.types.filter(
    (member) => (member.flags & (ts.TypeFlags.Null | ts.TypeFlags.Undefined)) === 0
  );
  if (nonNullish.length === 1 && nonNullish[0] !== void 0) {
    return nonNullish[0];
  }
  return type;
}
function isIntersectionWithBase(type, predicate) {
  return type.isIntersection() && type.types.some((member) => predicate(member));
}
function isNumberLike(type) {
  const stripped = stripNullishUnion(type);
  if (stripped.flags & (ts.TypeFlags.Number | ts.TypeFlags.NumberLiteral | ts.TypeFlags.BigInt | ts.TypeFlags.BigIntLiteral)) {
    return true;
  }
  if (stripped.isUnion()) {
    return stripped.types.every((member) => isNumberLike(member));
  }
  return isIntersectionWithBase(stripped, isNumberLike);
}
function isStringLike(type) {
  const stripped = stripNullishUnion(type);
  if (stripped.flags & (ts.TypeFlags.String | ts.TypeFlags.StringLiteral)) {
    return true;
  }
  if (stripped.isUnion()) {
    return stripped.types.every((member) => isStringLike(member));
  }
  return isIntersectionWithBase(stripped, isStringLike);
}
function isBooleanLike(type) {
  const stripped = stripNullishUnion(type);
  if (stripped.flags & (ts.TypeFlags.Boolean | ts.TypeFlags.BooleanLiteral)) {
    return true;
  }
  if (stripped.isUnion()) {
    return stripped.types.every((member) => isBooleanLike(member));
  }
  return isIntersectionWithBase(stripped, isBooleanLike);
}
function isNullLike(type) {
  const stripped = stripNullishUnion(type);
  if (stripped.flags & ts.TypeFlags.Null) {
    return true;
  }
  if (stripped.isUnion()) {
    return stripped.types.every((member) => isNullLike(member));
  }
  return isIntersectionWithBase(stripped, isNullLike);
}
function isArrayLike(type, checker) {
  const stripped = stripNullishUnion(type);
  if (checker.isArrayType(stripped)) {
    return true;
  }
  const symbol = stripped.getSymbol();
  return symbol?.name === "Array" || symbol?.name === "ReadonlyArray";
}
function isStringLiteralUnion(type) {
  const stripped = stripNullishUnion(type);
  return stripped.isUnion() && stripped.types.length > 0 && stripped.types.every(isStringLike);
}
function isObjectLike(type, checker) {
  const stripped = stripNullishUnion(type);
  return !isArrayLike(stripped, checker) && (stripped.flags & ts.TypeFlags.Object) !== 0;
}
function isJsonLike(type, checker) {
  const stripped = stripNullishUnion(type);
  if (isNullLike(stripped) || isNumberLike(stripped) || isStringLike(stripped) || isBooleanLike(stripped)) {
    return true;
  }
  if (stripped.isUnion()) {
    return stripped.types.every((member) => isJsonLike(member, checker));
  }
  if (isArrayLike(stripped, checker) || isObjectLike(stripped, checker)) {
    return true;
  }
  return isIntersectionWithBase(stripped, (member) => isJsonLike(member, checker));
}
function getArrayElementType(type, checker) {
  const stripped = stripNullishUnion(type);
  if (!checker.isArrayType(stripped)) {
    return null;
  }
  return checker.getTypeArguments(stripped)[0] ?? null;
}
function resolveDeclarationPlacement(node) {
  if (ts.isClassDeclaration(node)) return "class";
  if (ts.isPropertyDeclaration(node)) return "class-field";
  if (ts.isMethodDeclaration(node)) return "class-method";
  if (ts.isInterfaceDeclaration(node)) return "interface";
  if (ts.isPropertySignature(node)) return "interface-field";
  if (ts.isTypeAliasDeclaration(node)) return "type-alias";
  if (ts.isVariableDeclaration(node)) return "variable";
  if (ts.isFunctionDeclaration(node)) return "function";
  if (ts.isParameter(node)) {
    if (ts.isMethodDeclaration(node.parent) || ts.isConstructorDeclaration(node.parent)) {
      return "method-parameter";
    }
    return "function-parameter";
  }
  return null;
}
function getTypeSemanticCapabilities(type, checker) {
  const capabilities = /* @__PURE__ */ new Set();
  if (isNumberLike(type)) {
    capabilities.add("numeric-comparable");
  }
  if (isStringLike(type)) {
    capabilities.add("string-like");
  }
  if (isJsonLike(type, checker)) {
    capabilities.add("json-like");
  }
  if (isArrayLike(type, checker)) {
    capabilities.add("array-like");
  }
  if (isStringLiteralUnion(type)) {
    capabilities.add("enum-member-addressable");
  }
  if (isObjectLike(type, checker)) {
    capabilities.add("object-like");
  }
  return [...capabilities];
}
function hasTypeSemanticCapability(type, checker, capability) {
  return getTypeSemanticCapabilities(type, checker).includes(capability);
}
function resolvePathTargetType(type, checker, segments) {
  const stripped = stripNullishUnion(type);
  if (segments.length === 0) {
    return {
      kind: "resolved",
      type: stripped
    };
  }
  const arrayElementType = getArrayElementType(stripped, checker);
  if (arrayElementType !== null) {
    return resolvePathTargetType(arrayElementType, checker, segments);
  }
  if ((stripped.flags & ts.TypeFlags.Object) === 0) {
    return {
      kind: "unresolvable",
      type: stripped
    };
  }
  const [segment, ...rest] = segments;
  if (segment === void 0) {
    throw new Error("Invariant violation: path traversal requires a segment");
  }
  const property = stripped.getProperty(segment);
  if (property === void 0) {
    return {
      kind: "missing-property",
      segment
    };
  }
  const declaration = property.valueDeclaration ?? property.declarations?.[0];
  if (declaration === void 0) {
    return {
      kind: "unresolvable",
      type: stripped
    };
  }
  return resolvePathTargetType(
    checker.getTypeOfSymbolAtLocation(property, declaration),
    checker,
    rest
  );
}
function collectPropertyPaths(type, checker, capability, prefix, visited) {
  const stripped = stripNullishUnion(type);
  if (visited.has(stripped)) {
    return [];
  }
  visited.add(stripped);
  const suggestions = [];
  if (hasTypeSemanticCapability(stripped, checker, capability) && prefix.length > 0) {
    suggestions.push(prefix.join("."));
  }
  for (const property of stripped.getProperties()) {
    const declaration = property.valueDeclaration ?? property.declarations?.[0];
    if (declaration === void 0) {
      continue;
    }
    const propertyType = checker.getTypeOfSymbolAtLocation(property, declaration);
    const nextPrefix = [...prefix, property.name];
    suggestions.push(
      ...collectPropertyPaths(propertyType, checker, capability, nextPrefix, visited)
    );
  }
  return suggestions;
}
function collectCompatiblePathTargets(type, checker, capability) {
  return collectPropertyPaths(type, checker, capability, [], /* @__PURE__ */ new Set());
}

// src/source-bindings.ts
import * as ts2 from "typescript";
function getLastLeadingDocCommentRange(node, sourceFile) {
  const ranges = ts2.getLeadingCommentRanges(sourceFile.text, node.getFullStart()) ?? [];
  const docRanges = ranges.filter(
    (range) => sourceFile.text.slice(range.pos, range.end).startsWith("/**")
  );
  return docRanges.length === 0 ? null : docRanges[docRanges.length - 1] ?? null;
}
function getSubjectType(node, checker) {
  if (ts2.isClassDeclaration(node) || ts2.isInterfaceDeclaration(node) || ts2.isTypeAliasDeclaration(node) || ts2.isPropertyDeclaration(node) || ts2.isPropertySignature(node) || ts2.isMethodDeclaration(node) || ts2.isFunctionDeclaration(node) || ts2.isVariableDeclaration(node) || ts2.isParameter(node)) {
    return checker.getTypeAtLocation(node);
  }
  return void 0;
}
function getHostType(node, checker) {
  const parent = node.parent;
  if (ts2.isClassDeclaration(parent) || ts2.isInterfaceDeclaration(parent) || ts2.isTypeLiteralNode(parent) || ts2.isTypeAliasDeclaration(parent)) {
    return checker.getTypeAtLocation(parent);
  }
  return getSubjectType(node, checker);
}
function getDeclarationTypeParameterNames(node) {
  if (!(ts2.isClassDeclaration(node) || ts2.isInterfaceDeclaration(node) || ts2.isTypeAliasDeclaration(node) || ts2.isFunctionDeclaration(node) || ts2.isMethodDeclaration(node) || ts2.isConstructorDeclaration(node))) {
    return [];
  }
  return node.typeParameters?.flatMap(
    (parameter) => ts2.isIdentifier(parameter.name) ? [parameter.name.text] : []
  ) ?? [];
}
function getVisibleTypeParameterNames(node) {
  const names = /* @__PURE__ */ new Set();
  let current = node;
  for (; ; ) {
    for (const name of getDeclarationTypeParameterNames(current)) {
      names.add(name);
    }
    if (ts2.isSourceFile(current)) {
      break;
    }
    current = current.parent;
  }
  return [...names];
}
function getDirectPropertyTargets(node, checker) {
  const targets = [];
  const pushTarget = (member) => {
    if (!ts2.isIdentifier(member.name)) {
      return;
    }
    const declaration = member;
    targets.push({
      name: member.name.text,
      declaration,
      type: checker.getTypeAtLocation(member),
      optional: member.questionToken !== void 0
    });
  };
  if (ts2.isClassDeclaration(node)) {
    for (const member of node.members) {
      if (ts2.isPropertyDeclaration(member)) {
        pushTarget(member);
      }
    }
    return targets;
  }
  if (ts2.isInterfaceDeclaration(node)) {
    for (const member of node.members) {
      if (ts2.isPropertySignature(member)) {
        pushTarget(member);
      }
    }
    return targets;
  }
  if (ts2.isTypeAliasDeclaration(node) && ts2.isTypeLiteralNode(node.type)) {
    for (const member of node.type.members) {
      if (ts2.isPropertySignature(member)) {
        pushTarget(member);
      }
    }
  }
  return targets;
}
function findDeclarationForCommentOffset(sourceFile, offset) {
  let bestMatch = null;
  const visit = (node) => {
    if (resolveDeclarationPlacement(node) !== null) {
      const range = getLastLeadingDocCommentRange(node, sourceFile);
      if (range !== null && offset >= range.pos && offset <= range.end) {
        if (bestMatch === null || node.getWidth(sourceFile) < bestMatch.getWidth(sourceFile)) {
          bestMatch = node;
        }
      }
    }
    ts2.forEachChild(node, visit);
  };
  visit(sourceFile);
  return bestMatch;
}

// src/cursor-context.ts
function isWordChar(char) {
  return char !== void 0 && /[A-Za-z0-9]/u.test(char);
}
function isWhitespaceLike(char) {
  return char === void 0 || /\s/u.test(char) || char === "*";
}
function containsOffset(tag, offset) {
  return offset >= tag.tagNameSpan.start && offset <= tag.tagNameSpan.end;
}
function filterSignaturesByPlacement(signatures, placement) {
  if (placement === void 0 || placement === null) {
    return signatures;
  }
  const filtered = signatures.filter((signature) => signature.placements.includes(placement));
  return filtered.length > 0 ? filtered : signatures;
}
function hasExplicitTarget(signature) {
  return signature.parameters.some(
    (parameter) => parameter.kind === "target-path" || parameter.kind === "target-member" || parameter.kind === "target-variant"
  );
}
function matchesTargetKind(signature, targetKind) {
  return signature.parameters.some((parameter) => {
    switch (targetKind) {
      case "path":
        return parameter.kind === "target-path";
      case "member":
        return parameter.kind === "target-member";
      case "variant":
        return parameter.kind === "target-variant";
      default: {
        const exhaustive = targetKind;
        return exhaustive;
      }
    }
  });
}
function getContextualSignatures(tag, signatures) {
  const target = tag.target;
  if (target === null) {
    const untargeted = signatures.filter((signature) => !hasExplicitTarget(signature));
    return untargeted.length > 0 ? untargeted : signatures;
  }
  if (target.kind === "ambiguous") {
    return signatures;
  }
  const targetKind = target.kind;
  const targeted = signatures.filter((signature) => matchesTargetKind(signature, targetKind));
  return targeted.length > 0 ? targeted : signatures;
}
function getCompatiblePathTargetsForSignatures(signatures, checker, subjectType) {
  if (checker === void 0 || subjectType === void 0) {
    return [];
  }
  const suggestions = /* @__PURE__ */ new Set();
  for (const signature of signatures) {
    for (const parameter of signature.parameters) {
      if (parameter.kind !== "target-path" || parameter.capability === void 0) {
        continue;
      }
      for (const target of collectCompatiblePathTargets(
        subjectType,
        checker,
        parameter.capability
      )) {
        suggestions.add(target);
      }
    }
  }
  return [...suggestions].sort();
}
function getSupportedTargets(signatures) {
  const supportedTargets = /* @__PURE__ */ new Set(["none"]);
  for (const signature of signatures) {
    for (const parameter of signature.parameters) {
      switch (parameter.kind) {
        case "target-path":
          supportedTargets.add("path");
          break;
        case "target-member":
          supportedTargets.add("member");
          break;
        case "target-variant":
          supportedTargets.add("variant");
          break;
        default:
          break;
      }
    }
  }
  return [...supportedTargets];
}
function getTargetCompletions(signatures, compatiblePathTargets) {
  const completions = /* @__PURE__ */ new Set();
  for (const signature of signatures) {
    for (const parameter of signature.parameters) {
      switch (parameter.kind) {
        case "target-path":
          for (const target of compatiblePathTargets) {
            completions.add(target);
          }
          break;
        case "target-variant":
          completions.add("singular");
          completions.add("plural");
          break;
        default:
          break;
      }
    }
  }
  return [...completions];
}
function getDiscriminatorTargetCompletions(options) {
  if (options?.checker === void 0 || options.declaration === void 0) {
    return [];
  }
  return getDirectPropertyTargets(options.declaration, options.checker).map((target) => target.name);
}
function getDiscriminatorArgumentCompletions(options) {
  if (options?.declaration === void 0) {
    return [];
  }
  return getDeclarationTypeParameterNames(options.declaration);
}
function getCommentTagSemanticContext(tag, options) {
  const tagDefinition = getTagDefinition(tag.normalizedTagName, options?.extensions);
  const signatures = filterSignaturesByPlacement(
    tagDefinition?.signatures ?? [],
    options?.placement
  );
  const compatiblePathTargets = tagDefinition?.canonicalName === "discriminator" ? getDiscriminatorTargetCompletions(options) : getCompatiblePathTargetsForSignatures(signatures, options?.checker, options?.subjectType);
  const targetCompletions = tagDefinition?.canonicalName === "discriminator" ? compatiblePathTargets : getTargetCompletions(signatures, compatiblePathTargets);
  const contextualSignatures = getContextualSignatures(tag, signatures);
  const semantic = {
    tag,
    tagDefinition,
    placement: options?.placement ?? null,
    signatures,
    contextualSignatures,
    supportedTargets: getSupportedTargets(signatures),
    targetCompletions,
    compatiblePathTargets,
    valueLabels: getValueLabels(signatures),
    argumentCompletions: tagDefinition?.canonicalName === "discriminator" ? getDiscriminatorArgumentCompletions(options) : [],
    contextualTagHoverMarkdown: null,
    tagHoverMarkdown: tagDefinition?.hoverMarkdown ?? null,
    targetHoverMarkdown: null,
    argumentHoverMarkdown: null
  };
  return {
    ...semantic,
    contextualTagHoverMarkdown: buildContextualTagHoverMarkdown(semantic),
    targetHoverMarkdown: buildTargetHoverMarkdown(semantic),
    argumentHoverMarkdown: buildArgumentHoverMarkdown(semantic)
  };
}
function getValueLabels(signatures) {
  const labels = /* @__PURE__ */ new Set();
  for (const signature of signatures) {
    for (const parameter of signature.parameters) {
      if (parameter.kind === "value") {
        labels.add(parameter.label);
      }
    }
  }
  return [...labels];
}
function getTargetKindLabels(supportedTargets) {
  const labels = supportedTargets.filter((kind) => kind !== "none").map((kind) => `\`${kind}\``);
  return labels.length === 0 ? "none" : labels.join(", ");
}
function buildContextualTagHoverMarkdown(semantic) {
  if (semantic.tagDefinition === null) {
    return null;
  }
  const signatureLines = semantic.contextualSignatures.length === 1 ? [`**Relevant usage here:** \`${semantic.contextualSignatures[0]?.label ?? ""}\``] : semantic.contextualSignatures.length > 1 ? [
    "**Relevant usages here:**",
    ...semantic.contextualSignatures.map((signature) => `- \`${signature.label}\``)
  ] : [];
  return [
    `**@${semantic.tagDefinition.canonicalName}**`,
    "",
    semantic.tagDefinition.hoverSummary,
    ...signatureLines.length > 0 ? ["", ...signatureLines] : []
  ].join("\n");
}
function buildTargetHoverMarkdown(semantic) {
  if (semantic.tagDefinition === null) {
    return null;
  }
  const currentTarget = semantic.tag.target?.rawText ?? "";
  const lines = [
    `**Target for @${semantic.tagDefinition.canonicalName}**`,
    "",
    `Supported target forms: ${getTargetKindLabels(semantic.supportedTargets)}`
  ];
  if (currentTarget !== "") {
    lines.push("", `Current target: \`:${currentTarget}\``);
  }
  const MAX_HOVER_PATH_TARGETS = 8;
  if (semantic.compatiblePathTargets.length > 0) {
    lines.push("", "**Compatible path targets:**");
    for (const target of semantic.compatiblePathTargets.slice(0, MAX_HOVER_PATH_TARGETS)) {
      lines.push(`- \`:${target}\``);
    }
  } else if (semantic.supportedTargets.includes("variant")) {
    lines.push("", "Use `:singular` or `:plural` for variant-specific names.");
  } else if (semantic.supportedTargets.includes("path")) {
    lines.push(
      "",
      "Type-aware path completions become available when TypeScript binding is provided."
    );
  }
  return lines.join("\n");
}
function buildArgumentHoverMarkdown(semantic) {
  if (semantic.tagDefinition === null) {
    return null;
  }
  const valueLabels = getValueLabels(semantic.contextualSignatures);
  const formattedValueLabels = valueLabels.map((label) => `\`${label}\``);
  const formattedArgumentCompletions = semantic.argumentCompletions.map((label) => `\`${label}\``);
  const soleSignature = semantic.contextualSignatures.length === 1 ? semantic.contextualSignatures[0] : void 0;
  const signatureLines = semantic.contextualSignatures.length === 0 ? [] : soleSignature !== void 0 ? [`**Signature:** \`${soleSignature.label}\``] : [
    "**Signatures:**",
    ...semantic.contextualSignatures.map((signature) => `- \`${signature.label}\``)
  ];
  return [
    `**Argument for @${semantic.tagDefinition.canonicalName}**`,
    "",
    `Expected value: ${formattedValueLabels.join(" or ") || "`<value>`"}`,
    ...formattedArgumentCompletions.length > 0 ? ["", `Local type parameters: ${formattedArgumentCompletions.join(" or ")}`] : [],
    "",
    ...signatureLines
  ].join("\n");
}
function findEnclosingDocComment(documentText, offset, options) {
  const commentPattern = /\/\*\*[\s\S]*?\*\//gu;
  for (const match of documentText.matchAll(commentPattern)) {
    const fullMatch = match[0];
    const index = match.index;
    const start = index;
    const end = start + fullMatch.length;
    if (offset >= start && offset <= end) {
      return {
        text: fullMatch,
        start,
        end,
        parsed: parseCommentBlock(fullMatch, {
          offset: start,
          ...options?.extensions !== void 0 ? { extensions: options.extensions } : {}
        })
      };
    }
  }
  return null;
}
function findCommentTagAtOffset(documentText, offset, options) {
  const comment = findEnclosingDocComment(documentText, offset, options);
  if (comment === null) {
    return null;
  }
  return comment.parsed.tags.find((tag) => containsOffset(tag, offset)) ?? null;
}
function getCommentCursorTargetAtOffset(documentText, offset, options) {
  const comment = findEnclosingDocComment(documentText, offset, options);
  if (comment === null) {
    return null;
  }
  for (const tag of comment.parsed.tags) {
    if (containsOffset(tag, offset)) {
      return {
        kind: "tag-name",
        tag
      };
    }
    if (tag.colonSpan !== null && offset >= tag.colonSpan.start && offset <= tag.colonSpan.end) {
      return {
        kind: "colon",
        tag
      };
    }
    if (tag.target !== null && offset >= tag.target.span.start && offset <= tag.target.span.end) {
      return {
        kind: "target",
        tag
      };
    }
    if (tag.argumentSpan !== null && offset >= tag.argumentSpan.start && offset <= tag.argumentSpan.end) {
      return {
        kind: "argument",
        tag
      };
    }
  }
  return null;
}
function getTagCompletionPrefixAtOffset(documentText, offset) {
  const comment = findEnclosingDocComment(documentText, offset);
  if (comment === null) {
    return null;
  }
  const relativeOffset = offset - comment.start;
  if (relativeOffset < 0 || relativeOffset > comment.text.length) {
    return null;
  }
  let cursor = relativeOffset;
  while (cursor > 0 && isWordChar(comment.text[cursor - 1])) {
    cursor -= 1;
  }
  const atIndex = cursor - 1;
  if (atIndex < 0 || comment.text[atIndex] !== "@") {
    return null;
  }
  const previousChar = atIndex > 0 ? comment.text[atIndex - 1] : void 0;
  if (!isWhitespaceLike(previousChar)) {
    return null;
  }
  return comment.text.slice(cursor, relativeOffset);
}
function getCommentCompletionContextAtOffset(documentText, offset, options) {
  const prefix = getTagCompletionPrefixAtOffset(documentText, offset);
  if (prefix !== null) {
    return {
      kind: "tag-name",
      prefix
    };
  }
  const target = getCommentCursorTargetAtOffset(documentText, offset, options);
  if (target?.kind === "target" || target?.kind === "colon") {
    return {
      kind: "target",
      tag: target.tag
    };
  }
  if (target?.kind === "argument") {
    return {
      kind: "argument",
      tag: target.tag
    };
  }
  return {
    kind: "none"
  };
}
function getSemanticCommentCompletionContextAtOffset(documentText, offset, options) {
  const prefix = getTagCompletionPrefixAtOffset(documentText, offset);
  if (prefix !== null) {
    return {
      kind: "tag-name",
      prefix,
      availableTags: getAllTagDefinitions(options?.extensions)
    };
  }
  const target = getCommentCursorTargetAtOffset(
    documentText,
    offset,
    options?.extensions ? { extensions: options.extensions } : void 0
  );
  if (target?.kind === "target" || target?.kind === "colon") {
    return {
      kind: "target",
      semantic: getCommentTagSemanticContext(target.tag, options)
    };
  }
  if (target?.kind === "argument") {
    const semantic = getCommentTagSemanticContext(target.tag, options);
    return {
      kind: "argument",
      semantic,
      valueLabels: semantic.valueLabels
    };
  }
  return { kind: "none" };
}
function getCommentHoverInfoAtOffset(documentText, offset, options) {
  const target = getCommentCursorTargetAtOffset(
    documentText,
    offset,
    options?.extensions ? { extensions: options.extensions } : void 0
  );
  if (target === null) {
    return null;
  }
  const semantic = getCommentTagSemanticContext(target.tag, options);
  let markdown = null;
  switch (target.kind) {
    case "tag-name":
      markdown = semantic.contextualTagHoverMarkdown ?? semantic.tagHoverMarkdown;
      break;
    case "colon":
    case "target":
      markdown = semantic.targetHoverMarkdown;
      break;
    case "argument":
      markdown = semantic.argumentHoverMarkdown;
      break;
    default: {
      const exhaustive = target.kind;
      void exhaustive;
      break;
    }
  }
  return markdown === null ? null : {
    kind: target.kind === "colon" ? "target" : target.kind,
    markdown
  };
}

// src/tag-value-parser.ts
import {
  BUILTIN_CONSTRAINT_DEFINITIONS as BUILTIN_CONSTRAINT_DEFINITIONS2,
  isBuiltinConstraintName
} from "@formspec/core/internals";
var NUMERIC_CONSTRAINT_MAP = {
  minimum: "minimum",
  maximum: "maximum",
  exclusiveMinimum: "exclusiveMinimum",
  exclusiveMaximum: "exclusiveMaximum",
  multipleOf: "multipleOf"
};
var LENGTH_CONSTRAINT_MAP = {
  minLength: "minLength",
  maxLength: "maxLength",
  minItems: "minItems",
  maxItems: "maxItems"
};
function tryParseJson(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
function syntaxOptions(registry) {
  return registry?.extensions !== void 0 ? { extensions: registry.extensions } : void 0;
}
function parseConstraintTagValue(tagName, text, provenance, options) {
  const customConstraint = parseExtensionConstraintTagValue(tagName, text, provenance, options);
  if (customConstraint !== null) {
    return customConstraint;
  }
  if (!isBuiltinConstraintName(tagName)) {
    return null;
  }
  const parsedTag = parseTagSyntax(tagName, text, syntaxOptions(options?.registry));
  if (parsedTag.target !== null && !parsedTag.target.valid) {
    return null;
  }
  const effectiveText = parsedTag.argumentText;
  const path2 = parsedTag.target?.path ?? void 0;
  const expectedType = BUILTIN_CONSTRAINT_DEFINITIONS2[tagName];
  if (expectedType !== "boolean" && effectiveText.trim() === "") {
    return null;
  }
  if (expectedType === "number") {
    const value = Number(effectiveText);
    if (Number.isNaN(value)) {
      return null;
    }
    const numericKind = NUMERIC_CONSTRAINT_MAP[tagName];
    if (numericKind !== void 0) {
      return {
        kind: "constraint",
        constraintKind: numericKind,
        value,
        ...path2 !== void 0 && { path: path2 },
        provenance
      };
    }
    const lengthKind = LENGTH_CONSTRAINT_MAP[tagName];
    if (lengthKind !== void 0) {
      return {
        kind: "constraint",
        constraintKind: lengthKind,
        value,
        ...path2 !== void 0 && { path: path2 },
        provenance
      };
    }
    return null;
  }
  if (expectedType === "boolean") {
    const trimmed = effectiveText.trim();
    if (trimmed !== "" && trimmed !== "true") {
      return null;
    }
    if (tagName === "uniqueItems") {
      return {
        kind: "constraint",
        constraintKind: "uniqueItems",
        value: true,
        ...path2 !== void 0 && { path: path2 },
        provenance
      };
    }
    return null;
  }
  if (expectedType === "json") {
    if (tagName === "const") {
      const trimmedText = effectiveText.trim();
      if (trimmedText === "") {
        return null;
      }
      try {
        const parsed2 = JSON.parse(trimmedText);
        return {
          kind: "constraint",
          constraintKind: "const",
          value: parsed2,
          ...path2 !== void 0 && { path: path2 },
          provenance
        };
      } catch {
        return {
          kind: "constraint",
          constraintKind: "const",
          value: trimmedText,
          ...path2 !== void 0 && { path: path2 },
          provenance
        };
      }
    }
    const parsed = tryParseJson(effectiveText);
    if (!Array.isArray(parsed)) {
      return null;
    }
    const members = [];
    for (const item of parsed) {
      if (typeof item === "string" || typeof item === "number") {
        members.push(item);
        continue;
      }
      if (typeof item === "object" && item !== null && "id" in item) {
        const id = item["id"];
        if (typeof id === "string" || typeof id === "number") {
          members.push(id);
        }
      }
    }
    return {
      kind: "constraint",
      constraintKind: "allowedMembers",
      members,
      ...path2 !== void 0 && { path: path2 },
      provenance
    };
  }
  return {
    kind: "constraint",
    constraintKind: "pattern",
    pattern: effectiveText,
    ...path2 !== void 0 && { path: path2 },
    provenance
  };
}
function parseDefaultValueTagValue(text, provenance) {
  const trimmed = text.trim();
  let value;
  if (trimmed === "null") {
    value = null;
  } else if (trimmed === "true") {
    value = true;
  } else if (trimmed === "false") {
    value = false;
  } else {
    const parsed = tryParseJson(trimmed);
    value = parsed !== null ? parsed : trimmed;
  }
  return {
    kind: "annotation",
    annotationKind: "defaultValue",
    value,
    provenance
  };
}
function parseExtensionConstraintTagValue(tagName, text, provenance, options) {
  const parsedTag = parseTagSyntax(tagName, text, syntaxOptions(options?.registry));
  if (parsedTag.target !== null && !parsedTag.target.valid) {
    return null;
  }
  const effectiveText = parsedTag.argumentText;
  const path2 = parsedTag.target?.path ?? void 0;
  const registry = options?.registry;
  if (registry === void 0) {
    return null;
  }
  if (effectiveText.trim() === "") {
    return null;
  }
  const directTag = registry.findConstraintTag(tagName);
  if (directTag !== void 0) {
    return makeCustomConstraintNode(
      directTag.extensionId,
      directTag.registration.constraintName,
      directTag.registration.parseValue(effectiveText),
      provenance,
      path2,
      registry
    );
  }
  if (!isBuiltinConstraintName(tagName)) {
    return null;
  }
  const broadenedTypeId = getBroadenedCustomTypeId(options?.fieldType);
  if (broadenedTypeId === void 0) {
    return null;
  }
  const broadened = registry.findBuiltinConstraintBroadening(broadenedTypeId, tagName);
  if (broadened === void 0) {
    return null;
  }
  return makeCustomConstraintNode(
    broadened.extensionId,
    broadened.registration.constraintName,
    broadened.registration.parseValue(effectiveText),
    provenance,
    path2,
    registry
  );
}
function getBroadenedCustomTypeId(fieldType) {
  if (fieldType?.kind === "custom") {
    return fieldType.typeId;
  }
  if (fieldType?.kind !== "union") {
    return void 0;
  }
  const customMembers = fieldType.members.filter(
    (member) => member.kind === "custom"
  );
  if (customMembers.length !== 1) {
    return void 0;
  }
  const nonCustomMembers = fieldType.members.filter((member) => member.kind !== "custom");
  const allOtherMembersAreNull = nonCustomMembers.every(
    (member) => member.kind === "primitive" && member.primitiveKind === "null"
  );
  const customMember = customMembers[0];
  return allOtherMembersAreNull && customMember !== void 0 ? customMember.typeId : void 0;
}
function makeCustomConstraintNode(extensionId, constraintName, payload, provenance, path2, registry) {
  const constraintId = `${extensionId}/${constraintName}`;
  const registration = registry.findConstraint(constraintId);
  if (registration === void 0) {
    throw new Error(
      `Custom TSDoc tag resolved to unregistered constraint "${constraintId}". Register the constraint before using its tag.`
    );
  }
  return {
    kind: "constraint",
    constraintKind: "custom",
    constraintId,
    payload,
    compositionRule: registration.compositionRule,
    ...path2 !== void 0 && { path: path2 },
    provenance
  };
}

// src/semantic-targets.ts
import { normalizeConstraintTagName as normalizeConstraintTagName2 } from "@formspec/core/internals";
function pathKey(path2) {
  return path2?.segments.join(".") ?? "";
}
function formatConstraintTargetName(fieldName, path2) {
  if (path2 === null || path2.segments.length === 0) {
    return fieldName;
  }
  return `${fieldName}.${path2.segments.join(".")}`;
}
function dereferenceAnalysisType(type, typeRegistry) {
  let current = type;
  const seen = /* @__PURE__ */ new Set();
  while (current.kind === "reference") {
    if (seen.has(current.name)) {
      return current;
    }
    seen.add(current.name);
    const definition = typeRegistry[current.name];
    if (definition === void 0) {
      return current;
    }
    current = definition.type;
  }
  return current;
}
function collectReferencedTypeConstraints(type, typeRegistry) {
  const collected = [];
  let current = type;
  const seen = /* @__PURE__ */ new Set();
  while (current.kind === "reference") {
    if (seen.has(current.name)) {
      break;
    }
    seen.add(current.name);
    const definition = typeRegistry[current.name];
    if (definition === void 0) {
      break;
    }
    if (definition.constraints !== void 0) {
      collected.push(...definition.constraints);
    }
    current = definition.type;
  }
  return collected;
}
function collectReferencedTypeAnnotations(type, typeRegistry) {
  const collected = [];
  let current = type;
  const seen = /* @__PURE__ */ new Set();
  while (current.kind === "reference") {
    if (seen.has(current.name)) {
      break;
    }
    seen.add(current.name);
    const definition = typeRegistry[current.name];
    if (definition === void 0) {
      break;
    }
    if (definition.annotations !== void 0) {
      collected.push(...definition.annotations);
    }
    current = definition.type;
  }
  return collected;
}
function resolveProperty(type, typeRegistry, segments) {
  const effectiveType = dereferenceAnalysisType(type, typeRegistry);
  if (segments.length === 0) {
    return { kind: "resolved", property: null, rawType: type, type: effectiveType };
  }
  if (effectiveType.kind === "array") {
    return resolveProperty(effectiveType.items, typeRegistry, segments);
  }
  if (effectiveType.kind !== "object") {
    return { kind: "unresolvable", type: effectiveType };
  }
  const [segment, ...rest] = segments;
  if (segment === void 0) {
    throw new Error("Invariant violation: object traversal requires a segment");
  }
  const property = effectiveType.properties.find((candidate) => candidate.name === segment);
  if (property === void 0) {
    return { kind: "missing-property", segment };
  }
  if (rest.length === 0) {
    return {
      kind: "resolved",
      property,
      rawType: property.type,
      type: dereferenceAnalysisType(property.type, typeRegistry)
    };
  }
  return resolveProperty(property.type, typeRegistry, rest);
}
function resolveConstraintTargetState(fieldName, fieldType, path2, localConstraints, typeRegistry) {
  if (path2 === null) {
    const inheritedConstraints2 = collectReferencedTypeConstraints(fieldType, typeRegistry);
    const inheritedAnnotations2 = collectReferencedTypeAnnotations(fieldType, typeRegistry);
    const type = dereferenceAnalysisType(fieldType, typeRegistry);
    return {
      kind: "resolved",
      fieldName,
      path: path2,
      targetName: fieldName,
      type,
      inheritedConstraints: inheritedConstraints2,
      inheritedAnnotations: inheritedAnnotations2,
      localConstraints,
      effectiveConstraints: [...inheritedConstraints2, ...localConstraints]
    };
  }
  const resolution = resolveProperty(fieldType, typeRegistry, path2.segments);
  const targetName = formatConstraintTargetName(fieldName, path2);
  if (resolution.kind === "missing-property") {
    return {
      kind: "missing-property",
      fieldName,
      path: path2,
      targetName,
      segment: resolution.segment,
      localConstraints
    };
  }
  if (resolution.kind === "unresolvable") {
    return {
      kind: "unresolvable",
      fieldName,
      path: path2,
      targetName,
      type: resolution.type,
      localConstraints
    };
  }
  const propertyConstraints = resolution.property?.constraints ?? [];
  const propertyAnnotations = resolution.property?.annotations ?? [];
  const referencedConstraints = collectReferencedTypeConstraints(resolution.rawType, typeRegistry);
  const referencedAnnotations = collectReferencedTypeAnnotations(resolution.rawType, typeRegistry);
  const inheritedConstraints = [...propertyConstraints, ...referencedConstraints];
  const inheritedAnnotations = [...propertyAnnotations, ...referencedAnnotations];
  return {
    kind: "resolved",
    fieldName,
    path: path2,
    targetName,
    type: resolution.type,
    inheritedConstraints,
    inheritedAnnotations,
    localConstraints,
    effectiveConstraints: [...inheritedConstraints, ...localConstraints]
  };
}
function cloneTargetPath(path2) {
  if (path2 === void 0) {
    return null;
  }
  return { segments: [...path2.segments] };
}
function buildConstraintTargetStates(fieldName, fieldType, constraints, typeRegistry) {
  const grouped = /* @__PURE__ */ new Map([
    ["", { path: null, constraints: [] }]
  ]);
  for (const constraint of constraints) {
    const path2 = cloneTargetPath(constraint.path);
    const key = pathKey(path2);
    let bucket = grouped.get(key);
    if (bucket === void 0) {
      bucket = { path: path2, constraints: [] };
      grouped.set(key, bucket);
    }
    bucket.constraints.push(constraint);
  }
  return [...grouped.values()].map(
    (group) => resolveConstraintTargetState(fieldName, fieldType, group.path, group.constraints, typeRegistry)
  );
}
function addContradiction(diagnostics, message, primary, related) {
  diagnostics.push({
    code: "CONTRADICTING_CONSTRAINTS",
    message,
    severity: "error",
    primaryLocation: primary,
    relatedLocations: [related]
  });
}
function addTypeMismatch(diagnostics, message, primary) {
  diagnostics.push({
    code: "TYPE_MISMATCH",
    message,
    severity: "error",
    primaryLocation: primary,
    relatedLocations: []
  });
}
function addUnknownExtension(diagnostics, message, primary) {
  diagnostics.push({
    code: "UNKNOWN_EXTENSION",
    message,
    severity: "warning",
    primaryLocation: primary,
    relatedLocations: []
  });
}
function addUnknownPathTarget(diagnostics, message, primary) {
  diagnostics.push({
    code: "UNKNOWN_PATH_TARGET",
    message,
    severity: "error",
    primaryLocation: primary,
    relatedLocations: []
  });
}
function addConstraintBroadening(diagnostics, message, primary, related) {
  diagnostics.push({
    code: "CONSTRAINT_BROADENING",
    message,
    severity: "error",
    primaryLocation: primary,
    relatedLocations: [related]
  });
}
function getExtensionIdFromConstraintId(constraintId) {
  const separator = constraintId.lastIndexOf("/");
  if (separator <= 0) {
    return null;
  }
  return constraintId.slice(0, separator);
}
function typeLabel(type) {
  switch (type.kind) {
    case "primitive":
      return type.primitiveKind;
    case "enum":
      return "enum";
    case "array":
      return "array";
    case "object":
      return "object";
    case "record":
      return "record";
    case "union":
      return "union";
    case "reference":
      return `reference(${type.name})`;
    case "dynamic":
      return `dynamic(${type.dynamicKind})`;
    case "custom":
      return `custom(${type.typeId})`;
    default: {
      const exhaustive = type;
      return String(exhaustive);
    }
  }
}
function isJsonObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isJsonArray(value) {
  return Array.isArray(value);
}
function jsonValueEquals(left, right) {
  if (left === right) {
    return true;
  }
  if (isJsonArray(left) || isJsonArray(right)) {
    if (!isJsonArray(left) || !isJsonArray(right) || left.length !== right.length) {
      return false;
    }
    for (const [index, item] of left.entries()) {
      const rightItem = right[index];
      if (rightItem === void 0 || !jsonValueEquals(item, rightItem)) {
        return false;
      }
    }
    return true;
  }
  if (isJsonObject(left) || isJsonObject(right)) {
    if (!isJsonObject(left) || !isJsonObject(right)) {
      return false;
    }
    const leftKeys = Object.keys(left).sort();
    const rightKeys = Object.keys(right).sort();
    if (leftKeys.length !== rightKeys.length) {
      return false;
    }
    return leftKeys.every((key, index) => {
      const rightKey = rightKeys[index];
      if (rightKey !== key) {
        return false;
      }
      const leftValue = left[key];
      const rightValue = right[rightKey];
      return leftValue !== void 0 && rightValue !== void 0 && jsonValueEquals(leftValue, rightValue);
    });
  }
  return false;
}
function findNumeric(constraints, constraintKind) {
  return constraints.find(
    (constraint) => constraint.constraintKind === constraintKind
  );
}
function findLength(constraints, constraintKind) {
  return constraints.find(
    (constraint) => constraint.constraintKind === constraintKind
  );
}
function findAllowedMembers(constraints) {
  return constraints.filter(
    (constraint) => constraint.constraintKind === "allowedMembers"
  );
}
function findConstConstraints(constraints) {
  return constraints.filter(
    (constraint) => constraint.constraintKind === "const"
  );
}
function isOrderedBoundConstraint(constraint) {
  return constraint.constraintKind === "minimum" || constraint.constraintKind === "exclusiveMinimum" || constraint.constraintKind === "minLength" || constraint.constraintKind === "minItems" || constraint.constraintKind === "maximum" || constraint.constraintKind === "exclusiveMaximum" || constraint.constraintKind === "maxLength" || constraint.constraintKind === "maxItems";
}
function constraintPathKey(constraint) {
  return constraint.path?.segments.join(".") ?? "";
}
function orderedBoundFamily(kind) {
  switch (kind) {
    case "minimum":
    case "exclusiveMinimum":
      return "numeric-lower";
    case "maximum":
    case "exclusiveMaximum":
      return "numeric-upper";
    case "minLength":
      return "minLength";
    case "minItems":
      return "minItems";
    case "maxLength":
      return "maxLength";
    case "maxItems":
      return "maxItems";
    default: {
      const exhaustive = kind;
      return exhaustive;
    }
  }
}
function isNumericLowerKind(kind) {
  return kind === "minimum" || kind === "exclusiveMinimum";
}
function isNumericUpperKind(kind) {
  return kind === "maximum" || kind === "exclusiveMaximum";
}
function describeConstraintTag(constraint) {
  return `@${constraint.constraintKind}`;
}
function compareConstraintStrength(current, previous) {
  const family = orderedBoundFamily(current.constraintKind);
  if (family === "numeric-lower") {
    if (!isNumericLowerKind(current.constraintKind) || !isNumericLowerKind(previous.constraintKind)) {
      throw new Error("numeric-lower family received non-numeric lower-bound constraint");
    }
    if (current.value !== previous.value) {
      return current.value > previous.value ? 1 : -1;
    }
    if (current.constraintKind === "exclusiveMinimum" && previous.constraintKind === "minimum") {
      return 1;
    }
    if (current.constraintKind === "minimum" && previous.constraintKind === "exclusiveMinimum") {
      return -1;
    }
    return 0;
  }
  if (family === "numeric-upper") {
    if (!isNumericUpperKind(current.constraintKind) || !isNumericUpperKind(previous.constraintKind)) {
      throw new Error("numeric-upper family received non-numeric upper-bound constraint");
    }
    if (current.value !== previous.value) {
      return current.value < previous.value ? 1 : -1;
    }
    if (current.constraintKind === "exclusiveMaximum" && previous.constraintKind === "maximum") {
      return 1;
    }
    if (current.constraintKind === "maximum" && previous.constraintKind === "exclusiveMaximum") {
      return -1;
    }
    return 0;
  }
  switch (family) {
    case "minLength":
    case "minItems":
      if (current.value === previous.value) {
        return 0;
      }
      return current.value > previous.value ? 1 : -1;
    case "maxLength":
    case "maxItems":
      if (current.value === previous.value) {
        return 0;
      }
      return current.value < previous.value ? 1 : -1;
    default: {
      const exhaustive = family;
      return exhaustive;
    }
  }
}
function compareSemanticInclusivity(currentInclusive, previousInclusive) {
  if (currentInclusive === previousInclusive) {
    return 0;
  }
  return currentInclusive ? -1 : 1;
}
function compareCustomConstraintStrength(current, previous) {
  const order = current.comparePayloads(current.constraint.payload, previous.constraint.payload);
  const equalPayloadTiebreaker = order === 0 ? compareSemanticInclusivity(current.role.inclusive, previous.role.inclusive) : order;
  switch (current.role.bound) {
    case "lower":
      return equalPayloadTiebreaker;
    case "upper":
      return equalPayloadTiebreaker === 0 ? 0 : -equalPayloadTiebreaker;
    case "exact":
      return order === 0 ? 0 : Number.NaN;
    default: {
      const exhaustive = current.role.bound;
      return exhaustive;
    }
  }
}
function customConstraintsContradict(lower, upper) {
  const order = lower.comparePayloads(lower.constraint.payload, upper.constraint.payload);
  if (order > 0) {
    return true;
  }
  if (order < 0) {
    return false;
  }
  return !lower.role.inclusive || !upper.role.inclusive;
}
function describeCustomConstraintTag(constraint) {
  return constraint.provenance.tagName ?? constraint.constraintId;
}
function isNullType(type) {
  return type.kind === "primitive" && type.primitiveKind === "null";
}
function collectCustomConstraintCandidateTypes(type, typeRegistry) {
  const effectiveType = dereferenceAnalysisType(type, typeRegistry);
  const candidates = [effectiveType];
  if (effectiveType.kind === "array") {
    candidates.push(...collectCustomConstraintCandidateTypes(effectiveType.items, typeRegistry));
  }
  if (effectiveType.kind === "union") {
    const memberTypes = effectiveType.members.map(
      (member) => dereferenceAnalysisType(member, typeRegistry)
    );
    const nonNullMembers = memberTypes.filter((member) => !isNullType(member));
    if (nonNullMembers.length === 1 && nonNullMembers.length < memberTypes.length) {
      const [nullableMember] = nonNullMembers;
      if (nullableMember !== void 0) {
        candidates.push(...collectCustomConstraintCandidateTypes(nullableMember, typeRegistry));
      }
    }
  }
  return candidates;
}
function checkNumericContradictions(diagnostics, fieldName, constraints) {
  const min = findNumeric(constraints, "minimum");
  const max = findNumeric(constraints, "maximum");
  const exMin = findNumeric(constraints, "exclusiveMinimum");
  const exMax = findNumeric(constraints, "exclusiveMaximum");
  if (min !== void 0 && max !== void 0 && min.value > max.value) {
    addContradiction(
      diagnostics,
      `Field "${fieldName}": minimum (${String(min.value)}) is greater than maximum (${String(max.value)})`,
      min.provenance,
      max.provenance
    );
  }
  if (exMin !== void 0 && max !== void 0 && exMin.value >= max.value) {
    addContradiction(
      diagnostics,
      `Field "${fieldName}": exclusiveMinimum (${String(exMin.value)}) is greater than or equal to maximum (${String(max.value)})`,
      exMin.provenance,
      max.provenance
    );
  }
  if (min !== void 0 && exMax !== void 0 && min.value >= exMax.value) {
    addContradiction(
      diagnostics,
      `Field "${fieldName}": minimum (${String(min.value)}) is greater than or equal to exclusiveMaximum (${String(exMax.value)})`,
      min.provenance,
      exMax.provenance
    );
  }
  if (exMin !== void 0 && exMax !== void 0 && exMin.value >= exMax.value) {
    addContradiction(
      diagnostics,
      `Field "${fieldName}": exclusiveMinimum (${String(exMin.value)}) is greater than or equal to exclusiveMaximum (${String(exMax.value)})`,
      exMin.provenance,
      exMax.provenance
    );
  }
}
function checkLengthContradictions(diagnostics, fieldName, constraints) {
  const minLen = findLength(constraints, "minLength");
  const maxLen = findLength(constraints, "maxLength");
  if (minLen !== void 0 && maxLen !== void 0 && minLen.value > maxLen.value) {
    addContradiction(
      diagnostics,
      `Field "${fieldName}": minLength (${String(minLen.value)}) is greater than maxLength (${String(maxLen.value)})`,
      minLen.provenance,
      maxLen.provenance
    );
  }
  const minItems = findLength(constraints, "minItems");
  const maxItems = findLength(constraints, "maxItems");
  if (minItems !== void 0 && maxItems !== void 0 && minItems.value > maxItems.value) {
    addContradiction(
      diagnostics,
      `Field "${fieldName}": minItems (${String(minItems.value)}) is greater than maxItems (${String(maxItems.value)})`,
      minItems.provenance,
      maxItems.provenance
    );
  }
}
function checkAllowedMembersContradiction(diagnostics, fieldName, constraints) {
  const members = findAllowedMembers(constraints);
  if (members.length < 2) {
    return;
  }
  const firstSet = new Set(members[0]?.members ?? []);
  for (let index = 1; index < members.length; index += 1) {
    const current = members[index];
    if (current === void 0) {
      continue;
    }
    for (const member of firstSet) {
      if (!current.members.includes(member)) {
        firstSet.delete(member);
      }
    }
  }
  if (firstSet.size === 0) {
    const first = members[0];
    const second = members[1];
    if (first !== void 0 && second !== void 0) {
      addContradiction(
        diagnostics,
        `Field "${fieldName}": allowedMembers constraints have an empty intersection (no valid values remain)`,
        first.provenance,
        second.provenance
      );
    }
  }
}
function checkConstContradictions(diagnostics, fieldName, constraints) {
  const constConstraints = findConstConstraints(constraints);
  if (constConstraints.length < 2) {
    return;
  }
  const first = constConstraints[0];
  if (first === void 0) {
    return;
  }
  for (let index = 1; index < constConstraints.length; index += 1) {
    const current = constConstraints[index];
    if (current === void 0 || jsonValueEquals(first.value, current.value)) {
      continue;
    }
    addContradiction(
      diagnostics,
      `Field "${fieldName}": conflicting @const constraints require both ${JSON.stringify(first.value)} and ${JSON.stringify(current.value)}`,
      first.provenance,
      current.provenance
    );
  }
}
function checkConstraintBroadening(diagnostics, fieldName, constraints) {
  const strongestByKey = /* @__PURE__ */ new Map();
  for (const constraint of constraints) {
    if (!isOrderedBoundConstraint(constraint)) {
      continue;
    }
    const key = `${orderedBoundFamily(constraint.constraintKind)}:${constraintPathKey(constraint)}`;
    const previous = strongestByKey.get(key);
    if (previous === void 0) {
      strongestByKey.set(key, constraint);
      continue;
    }
    const strength = compareConstraintStrength(constraint, previous);
    if (strength < 0) {
      addConstraintBroadening(
        diagnostics,
        `Field "${fieldName}": ${describeConstraintTag(constraint)} (${String(constraint.value)}) is broader than earlier ${describeConstraintTag(previous)} (${String(previous.value)}). Constraints can only narrow.`,
        constraint.provenance,
        previous.provenance
      );
      continue;
    }
    if (strength > 0) {
      strongestByKey.set(key, constraint);
    }
  }
}
function checkCustomConstraintSemantics(diagnostics, fieldName, constraints, extensionRegistry) {
  if (extensionRegistry === void 0) {
    return;
  }
  const strongestByKey = /* @__PURE__ */ new Map();
  const lowerByFamily = /* @__PURE__ */ new Map();
  const upperByFamily = /* @__PURE__ */ new Map();
  for (const constraint of constraints) {
    if (constraint.constraintKind !== "custom") {
      continue;
    }
    const registration = extensionRegistry.findConstraint(constraint.constraintId);
    if (registration?.comparePayloads === void 0 || registration.semanticRole === void 0) {
      continue;
    }
    const entry = {
      constraint,
      comparePayloads: registration.comparePayloads,
      role: registration.semanticRole
    };
    const familyKey = `${registration.semanticRole.family}:${constraintPathKey(constraint)}`;
    const boundKey = `${familyKey}:${registration.semanticRole.bound}`;
    const previous = strongestByKey.get(boundKey);
    if (previous !== void 0) {
      const strength = compareCustomConstraintStrength(entry, previous);
      if (Number.isNaN(strength)) {
        addContradiction(
          diagnostics,
          `Field "${fieldName}": ${describeCustomConstraintTag(constraint)} conflicts with ${describeCustomConstraintTag(previous.constraint)}`,
          constraint.provenance,
          previous.constraint.provenance
        );
        continue;
      }
      if (strength < 0) {
        addConstraintBroadening(
          diagnostics,
          `Field "${fieldName}": ${describeCustomConstraintTag(constraint)} is broader than earlier ${describeCustomConstraintTag(previous.constraint)}. Constraints can only narrow.`,
          constraint.provenance,
          previous.constraint.provenance
        );
        continue;
      }
      if (strength > 0) {
        strongestByKey.set(boundKey, entry);
      }
    } else {
      strongestByKey.set(boundKey, entry);
    }
    if (registration.semanticRole.bound === "lower") {
      lowerByFamily.set(familyKey, strongestByKey.get(boundKey) ?? entry);
    } else if (registration.semanticRole.bound === "upper") {
      upperByFamily.set(familyKey, strongestByKey.get(boundKey) ?? entry);
    }
  }
  for (const [familyKey, lower] of lowerByFamily) {
    const upper = upperByFamily.get(familyKey);
    if (upper === void 0 || !customConstraintsContradict(lower, upper)) {
      continue;
    }
    addContradiction(
      diagnostics,
      `Field "${fieldName}": ${describeCustomConstraintTag(lower.constraint)} contradicts ${describeCustomConstraintTag(upper.constraint)}`,
      lower.constraint.provenance,
      upper.constraint.provenance
    );
  }
}
function checkCustomConstraint(diagnostics, fieldName, type, constraint, typeRegistry, extensionRegistry) {
  if (extensionRegistry === void 0) {
    return;
  }
  const registration = extensionRegistry.findConstraint(constraint.constraintId);
  if (registration === void 0) {
    addUnknownExtension(
      diagnostics,
      `Field "${fieldName}": custom constraint "${constraint.constraintId}" is not registered in the extension registry`,
      constraint.provenance
    );
    return;
  }
  const candidateTypes = collectCustomConstraintCandidateTypes(type, typeRegistry);
  const normalizedTagName = constraint.provenance.tagName === void 0 ? void 0 : normalizeConstraintTagName2(constraint.provenance.tagName.replace(/^@/, ""));
  if (normalizedTagName !== void 0) {
    const tagRegistration = extensionRegistry.findConstraintTag(normalizedTagName);
    const extensionId = getExtensionIdFromConstraintId(constraint.constraintId);
    if (extensionId !== null && tagRegistration?.extensionId === extensionId && tagRegistration.registration.constraintName === registration.constraintName && !candidateTypes.some(
      (candidateType) => tagRegistration.registration.isApplicableToType?.(candidateType) !== false
    )) {
      addTypeMismatch(
        diagnostics,
        `Field "${fieldName}": custom constraint "${constraint.constraintId}" is not applicable to type "${typeLabel(type)}"`,
        constraint.provenance
      );
      return;
    }
  }
  if (registration.applicableTypes === null) {
    if (!candidateTypes.some(
      (candidateType) => registration.isApplicableToType?.(candidateType) !== false
    )) {
      addTypeMismatch(
        diagnostics,
        `Field "${fieldName}": custom constraint "${constraint.constraintId}" is not applicable to type "${typeLabel(type)}"`,
        constraint.provenance
      );
    }
    return;
  }
  const applicableTypes = registration.applicableTypes;
  const matchesApplicableType = candidateTypes.some(
    (candidateType) => applicableTypes.includes(candidateType.kind) && registration.isApplicableToType?.(candidateType) !== false
  );
  if (!matchesApplicableType) {
    addTypeMismatch(
      diagnostics,
      `Field "${fieldName}": custom constraint "${constraint.constraintId}" is not applicable to type "${typeLabel(type)}"`,
      constraint.provenance
    );
  }
}
function checkConstraintOnType(diagnostics, fieldName, type, constraint, typeRegistry, extensionRegistry) {
  const effectiveType = dereferenceAnalysisType(type, typeRegistry);
  const isNumber = effectiveType.kind === "primitive" && ["number", "integer", "bigint"].includes(effectiveType.primitiveKind);
  const isString = effectiveType.kind === "primitive" && effectiveType.primitiveKind === "string";
  const isArray = effectiveType.kind === "array";
  const isEnum = effectiveType.kind === "enum";
  const arrayItemType = effectiveType.kind === "array" ? dereferenceAnalysisType(effectiveType.items, typeRegistry) : void 0;
  const isStringArray2 = arrayItemType?.kind === "primitive" && arrayItemType.primitiveKind === "string";
  const label = typeLabel(effectiveType);
  switch (constraint.constraintKind) {
    case "minimum":
    case "maximum":
    case "exclusiveMinimum":
    case "exclusiveMaximum":
    case "multipleOf":
      if (!isNumber) {
        addTypeMismatch(
          diagnostics,
          `Field "${fieldName}": constraint "${constraint.constraintKind}" is only valid on number fields, but field type is "${label}"`,
          constraint.provenance
        );
      }
      break;
    case "minLength":
    case "maxLength":
    case "pattern":
      if (!isString && !isStringArray2) {
        addTypeMismatch(
          diagnostics,
          `Field "${fieldName}": constraint "${constraint.constraintKind}" is only valid on string fields or string array items, but field type is "${label}"`,
          constraint.provenance
        );
      }
      break;
    case "minItems":
    case "maxItems":
    case "uniqueItems":
      if (!isArray) {
        addTypeMismatch(
          diagnostics,
          `Field "${fieldName}": constraint "${constraint.constraintKind}" is only valid on array fields, but field type is "${label}"`,
          constraint.provenance
        );
      }
      break;
    case "allowedMembers":
      if (!isEnum) {
        addTypeMismatch(
          diagnostics,
          `Field "${fieldName}": constraint "allowedMembers" is only valid on enum fields, but field type is "${label}"`,
          constraint.provenance
        );
      }
      break;
    case "const": {
      const isPrimitiveConstType = effectiveType.kind === "primitive" && ["string", "number", "integer", "bigint", "boolean", "null"].includes(
        effectiveType.primitiveKind
      ) || effectiveType.kind === "enum";
      if (!isPrimitiveConstType) {
        addTypeMismatch(
          diagnostics,
          `Field "${fieldName}": constraint "const" is only valid on primitive or enum fields, but field type is "${label}"`,
          constraint.provenance
        );
        break;
      }
      if (effectiveType.kind === "primitive") {
        const valueType = constraint.value === null ? "null" : Array.isArray(constraint.value) ? "array" : typeof constraint.value;
        const expectedValueType = effectiveType.primitiveKind === "integer" || effectiveType.primitiveKind === "bigint" ? "number" : effectiveType.primitiveKind;
        if (valueType !== expectedValueType) {
          addTypeMismatch(
            diagnostics,
            `Field "${fieldName}": @const value type "${valueType}" is incompatible with field type "${effectiveType.primitiveKind}"`,
            constraint.provenance
          );
        }
        break;
      }
      const memberValues = effectiveType.members.map((member) => member.value);
      if (!memberValues.some((member) => jsonValueEquals(member, constraint.value))) {
        addTypeMismatch(
          diagnostics,
          `Field "${fieldName}": @const value ${JSON.stringify(constraint.value)} is not one of the enum members`,
          constraint.provenance
        );
      }
      break;
    }
    case "custom":
      checkCustomConstraint(
        diagnostics,
        fieldName,
        effectiveType,
        constraint,
        typeRegistry,
        extensionRegistry
      );
      break;
    default: {
      const exhaustive = constraint;
      throw new Error(`Unhandled constraint: ${JSON.stringify(exhaustive)}`);
    }
  }
}
function analyzeResolvedTargetState(diagnostics, state, typeRegistry, extensionRegistry) {
  checkNumericContradictions(diagnostics, state.targetName, state.effectiveConstraints);
  checkLengthContradictions(diagnostics, state.targetName, state.effectiveConstraints);
  checkAllowedMembersContradiction(diagnostics, state.targetName, state.effectiveConstraints);
  checkConstContradictions(diagnostics, state.targetName, state.effectiveConstraints);
  checkConstraintBroadening(diagnostics, state.targetName, state.effectiveConstraints);
  checkCustomConstraintSemantics(
    diagnostics,
    state.targetName,
    state.effectiveConstraints,
    extensionRegistry
  );
  for (const constraint of state.effectiveConstraints) {
    checkConstraintOnType(
      diagnostics,
      state.targetName,
      state.type,
      constraint,
      typeRegistry,
      extensionRegistry
    );
  }
}
function analyzeConstraintTargets(fieldName, fieldType, constraints, typeRegistry, options) {
  const diagnostics = [];
  const targetStates = buildConstraintTargetStates(fieldName, fieldType, constraints, typeRegistry);
  for (const targetState of targetStates) {
    switch (targetState.kind) {
      case "resolved":
        analyzeResolvedTargetState(
          diagnostics,
          targetState,
          typeRegistry,
          options?.extensionRegistry
        );
        break;
      case "missing-property":
        for (const constraint of targetState.localConstraints) {
          addUnknownPathTarget(
            diagnostics,
            `Field "${targetState.targetName}": path-targeted constraint "${constraint.constraintKind}" references unknown path segment "${targetState.segment}"`,
            constraint.provenance
          );
        }
        break;
      case "unresolvable":
        for (const constraint of targetState.localConstraints) {
          addTypeMismatch(
            diagnostics,
            `Field "${targetState.targetName}": path-targeted constraint "${constraint.constraintKind}" is invalid because type "${typeLabel(targetState.type)}" cannot be traversed`,
            constraint.provenance
          );
        }
        break;
      default: {
        const exhaustive = targetState;
        throw new Error(`Unhandled target state: ${String(exhaustive)}`);
      }
    }
  }
  return {
    diagnostics,
    targetStates
  };
}

// src/constants.ts
var FORM_SPEC_SYNTHETIC_BATCH_CACHE_ENTRIES = 64;

// src/lru-cache.ts
var LruCache = class {
  constructor(maxEntries) {
    this.maxEntries = maxEntries;
  }
  map = /* @__PURE__ */ new Map();
  get(key) {
    const cached = this.map.get(key);
    if (cached === void 0) {
      return void 0;
    }
    this.map.delete(key);
    this.map.set(key, cached);
    return cached;
  }
  set(key, value) {
    if (this.map.has(key)) {
      this.map.delete(key);
    }
    this.map.set(key, value);
    if (this.map.size <= this.maxEntries) {
      return;
    }
    const oldestKey = this.map.keys().next().value;
    if (oldestKey !== void 0) {
      this.map.delete(oldestKey);
    }
  }
};

// src/workspace-runtime.ts
import path from "path";

// src/semantic-protocol.ts
var FORMSPEC_ANALYSIS_PROTOCOL_VERSION = 4;
var FORMSPEC_ANALYSIS_SCHEMA_VERSION = 2;
function isObjectRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isCommentSpan(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return typeof candidate.start === "number" && typeof candidate.end === "number";
}
function isStringArray(value) {
  return Array.isArray(value) && value.every((entry) => typeof entry === "string");
}
function isNumberArray(value) {
  return Array.isArray(value) && value.every((entry) => typeof entry === "number");
}
function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}
function isBooleanArray(value) {
  return Array.isArray(value) && value.every((entry) => typeof entry === "boolean");
}
function isJsonValue(value) {
  if (value === null || typeof value === "string" || typeof value === "boolean") {
    return true;
  }
  if (isFiniteNumber(value)) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.every(isJsonValue);
  }
  if (!isObjectRecord(value)) {
    return false;
  }
  return Object.values(value).every(isJsonValue);
}
function isDiagnosticDataValue(value) {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean" || isStringArray(value) || isNumberArray(value) || isBooleanArray(value);
}
function isDiagnosticDataRecord(value) {
  return isObjectRecord(value) && Object.values(value).every(isDiagnosticDataValue);
}
var FORM_SPEC_PLACEMENT_VALUES = new Set(FORM_SPEC_PLACEMENTS);
var FORM_SPEC_TARGET_KIND_VALUES = new Set(FORM_SPEC_TARGET_KINDS);
function isPlacementValue(value) {
  return typeof value === "string" && FORM_SPEC_PLACEMENT_VALUES.has(value);
}
function isTargetKindValue(value) {
  return typeof value === "string" && FORM_SPEC_TARGET_KIND_VALUES.has(value);
}
function isPlacementArray(value) {
  return Array.isArray(value) && value.every(isPlacementValue);
}
function isTargetKindArray(value) {
  return Array.isArray(value) && value.every(isTargetKindValue);
}
function isDiagnosticCategory(value) {
  return value === "tag-recognition" || value === "value-parsing" || value === "type-compatibility" || value === "target-resolution" || value === "constraint-validation" || value === "infrastructure";
}
function isIpcEndpoint(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return (candidate.kind === "unix-socket" || candidate.kind === "windows-pipe") && typeof candidate.address === "string";
}
function isSerializedTagDefinition(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return typeof candidate.canonicalName === "string" && typeof candidate.completionDetail === "string" && typeof candidate.hoverMarkdown === "string";
}
function isSerializedTagSignature(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return typeof candidate.label === "string" && isPlacementArray(candidate.placements);
}
function isSerializedCommentTargetSpecifier(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return typeof candidate.rawText === "string" && typeof candidate.valid === "boolean" && typeof candidate.kind === "string" && isCommentSpan(candidate.fullSpan) && isCommentSpan(candidate.colonSpan) && isCommentSpan(candidate.span);
}
function isSerializedTagSemanticContext(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return typeof candidate.tagName === "string" && (candidate.tagDefinition === null || isSerializedTagDefinition(candidate.tagDefinition)) && (candidate.placement === null || isPlacementValue(candidate.placement)) && Array.isArray(candidate.contextualSignatures) && candidate.contextualSignatures.every(isSerializedTagSignature) && isTargetKindArray(candidate.supportedTargets) && isStringArray(candidate.targetCompletions) && isStringArray(candidate.compatiblePathTargets) && isStringArray(candidate.valueLabels) && isStringArray(candidate.argumentCompletions) && Array.isArray(candidate.signatures) && candidate.signatures.every(isSerializedTagSignature) && (candidate.contextualTagHoverMarkdown === null || typeof candidate.contextualTagHoverMarkdown === "string") && (candidate.tagHoverMarkdown === null || typeof candidate.tagHoverMarkdown === "string") && (candidate.targetHoverMarkdown === null || typeof candidate.targetHoverMarkdown === "string") && (candidate.argumentHoverMarkdown === null || typeof candidate.argumentHoverMarkdown === "string");
}
function isSerializedCompletionContext(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  if (typeof candidate.kind !== "string") {
    return false;
  }
  switch (candidate.kind) {
    case "tag-name":
      return typeof candidate.prefix === "string" && Array.isArray(candidate.availableTags) ? candidate.availableTags.every(isSerializedTagDefinition) : false;
    case "target":
      return isSerializedTagSemanticContext(candidate.semantic);
    case "argument":
      return isSerializedTagSemanticContext(candidate.semantic) && isStringArray(candidate.valueLabels);
    case "none":
      return true;
    default:
      return false;
  }
}
function isSerializedHoverInfo(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return (candidate.kind === "tag-name" || candidate.kind === "target" || candidate.kind === "argument" || candidate.kind === "declaration") && typeof candidate.markdown === "string";
}
function hasCurrentProtocolVersion(value) {
  return isObjectRecord(value) && value["protocolVersion"] === FORMSPEC_ANALYSIS_PROTOCOL_VERSION;
}
function isAnalysisDiagnosticLocation(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return typeof candidate.filePath === "string" && isCommentSpan(candidate.range) && (candidate.message === void 0 || typeof candidate.message === "string");
}
function isAnalysisDiagnostic(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return typeof candidate.code === "string" && isDiagnosticCategory(candidate.category) && typeof candidate.message === "string" && isCommentSpan(candidate.range) && (candidate.severity === "error" || candidate.severity === "warning" || candidate.severity === "info") && Array.isArray(candidate.relatedLocations) && candidate.relatedLocations.every(isAnalysisDiagnosticLocation) && isDiagnosticDataRecord(candidate.data);
}
function isAnalysisTagSnapshot(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return typeof candidate.rawTagName === "string" && typeof candidate.normalizedTagName === "string" && typeof candidate.recognized === "boolean" && isCommentSpan(candidate.fullSpan) && isCommentSpan(candidate.tagNameSpan) && (candidate.payloadSpan === null || isCommentSpan(candidate.payloadSpan)) && (candidate.target === null || isSerializedCommentTargetSpecifier(candidate.target)) && (candidate.argumentSpan === null || isCommentSpan(candidate.argumentSpan)) && typeof candidate.argumentText === "string" && isSerializedTagSemanticContext(candidate.semantic);
}
function isExplicitMetadataSource(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return typeof candidate.tagName === "string" && (candidate.form === "bare" || candidate.form === "qualified") && isCommentSpan(candidate.fullRange) && isCommentSpan(candidate.tagNameRange) && (candidate.qualifierRange === void 0 || isCommentSpan(candidate.qualifierRange)) && isCommentSpan(candidate.valueRange) && (candidate.qualifier === void 0 || typeof candidate.qualifier === "string");
}
function isResolvedScalarMetadata(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return typeof candidate.value === "string" && (candidate.source === "explicit" || candidate.source === "inferred");
}
function isResolvedMetadata(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return (candidate.apiName === void 0 || isResolvedScalarMetadata(candidate.apiName)) && (candidate.displayName === void 0 || isResolvedScalarMetadata(candidate.displayName)) && (candidate.apiNamePlural === void 0 || isResolvedScalarMetadata(candidate.apiNamePlural)) && (candidate.displayNamePlural === void 0 || isResolvedScalarMetadata(candidate.displayNamePlural));
}
function isMetadataEntry(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return typeof candidate.slotId === "string" && typeof candidate.tagName === "string" && (candidate.qualifier === void 0 || typeof candidate.qualifier === "string") && typeof candidate.value === "string" && (candidate.source === "explicit" || candidate.source === "inferred") && (candidate.explicitSource === void 0 || isExplicitMetadataSource(candidate.explicitSource));
}
function isDeclarationFact(value) {
  if (!isObjectRecord(value) || typeof value["kind"] !== "string") {
    return false;
  }
  const candidate = value;
  switch (candidate.kind) {
    case "description":
    case "remarks":
    case "example":
      return typeof candidate.value === "string";
    case "default-value":
      return isJsonValue(candidate.value);
    case "deprecated":
      return candidate.message === null || typeof candidate.message === "string";
    case "numeric-constraints":
      return (candidate.targetPath === null || typeof candidate.targetPath === "string") && (candidate.minimum === void 0 || isFiniteNumber(candidate.minimum)) && (candidate.maximum === void 0 || isFiniteNumber(candidate.maximum)) && (candidate.exclusiveMinimum === void 0 || isFiniteNumber(candidate.exclusiveMinimum)) && (candidate.exclusiveMaximum === void 0 || isFiniteNumber(candidate.exclusiveMaximum)) && (candidate.multipleOf === void 0 || isFiniteNumber(candidate.multipleOf));
    case "string-constraints":
      return (candidate.targetPath === null || typeof candidate.targetPath === "string") && (candidate.minLength === void 0 || isFiniteNumber(candidate.minLength)) && (candidate.maxLength === void 0 || isFiniteNumber(candidate.maxLength)) && isStringArray(candidate.patterns);
    case "array-constraints":
      return (candidate.targetPath === null || typeof candidate.targetPath === "string") && (candidate.minItems === void 0 || isFiniteNumber(candidate.minItems)) && (candidate.maxItems === void 0 || isFiniteNumber(candidate.maxItems)) && (candidate.uniqueItems === void 0 || typeof candidate.uniqueItems === "boolean");
    case "allowed-members":
      return (candidate.targetPath === null || typeof candidate.targetPath === "string") && Array.isArray(candidate.members) && candidate.members.every(
        (member) => typeof member === "string" || isFiniteNumber(member)
      );
    case "const":
      return (candidate.targetPath === null || typeof candidate.targetPath === "string") && isJsonValue(candidate.value);
    case "custom-constraint":
      return (candidate.targetPath === null || typeof candidate.targetPath === "string") && typeof candidate.constraintId === "string" && (candidate.compositionRule === "intersect" || candidate.compositionRule === "override") && isJsonValue(candidate.payload);
    default:
      return false;
  }
}
function isDeclarationSummary(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return (candidate.summaryText === null || typeof candidate.summaryText === "string") && (candidate.resolvedMetadata === null || isResolvedMetadata(candidate.resolvedMetadata)) && Array.isArray(candidate.metadataEntries) && candidate.metadataEntries.every(isMetadataEntry) && Array.isArray(candidate.facts) && candidate.facts.every(isDeclarationFact) && typeof candidate.hoverMarkdown === "string";
}
function isAnalysisCommentSnapshot(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return isCommentSpan(candidate.commentSpan) && isCommentSpan(candidate.declarationSpan) && (candidate.placement === null || isPlacementValue(candidate.placement)) && (candidate.subjectType === null || typeof candidate.subjectType === "string") && (candidate.hostType === null || typeof candidate.hostType === "string") && isDeclarationSummary(candidate.declarationSummary) && Array.isArray(candidate.tags) && candidate.tags.every(isAnalysisTagSnapshot);
}
function isAnalysisFileSnapshot(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return typeof candidate.filePath === "string" && typeof candidate.sourceHash === "string" && typeof candidate.generatedAt === "string" && Array.isArray(candidate.comments) && candidate.comments.every(isAnalysisCommentSnapshot) && Array.isArray(candidate.diagnostics) && candidate.diagnostics.every(isAnalysisDiagnostic);
}
function isFormSpecAnalysisManifest(value) {
  if (!isObjectRecord(value)) {
    return false;
  }
  const candidate = value;
  return candidate.protocolVersion === FORMSPEC_ANALYSIS_PROTOCOL_VERSION && candidate.analysisSchemaVersion === FORMSPEC_ANALYSIS_SCHEMA_VERSION && typeof candidate.workspaceRoot === "string" && typeof candidate.workspaceId === "string" && isIpcEndpoint(candidate.endpoint) && typeof candidate.typescriptVersion === "string" && typeof candidate.extensionFingerprint === "string" && typeof candidate.generation === "number" && typeof candidate.updatedAt === "string";
}
function isFormSpecSemanticQuery(value) {
  if (!hasCurrentProtocolVersion(value)) {
    return false;
  }
  const candidate = value;
  if (typeof candidate.kind !== "string") {
    return false;
  }
  switch (candidate.kind) {
    case "health":
      return true;
    case "completion":
    case "hover":
      return typeof candidate.filePath === "string" && typeof candidate.offset === "number";
    case "diagnostics":
    case "file-snapshot":
      return typeof candidate.filePath === "string";
    default:
      return false;
  }
}
function isFormSpecSemanticResponse(value) {
  if (!hasCurrentProtocolVersion(value)) {
    return false;
  }
  const candidate = value;
  if (typeof candidate.kind !== "string") {
    return false;
  }
  switch (candidate.kind) {
    case "health":
      return isFormSpecAnalysisManifest(candidate.manifest);
    case "completion":
      return typeof candidate.sourceHash === "string" && isSerializedCompletionContext(candidate.context);
    case "hover":
      return typeof candidate.sourceHash === "string" && (candidate.hover === null || isSerializedHoverInfo(candidate.hover));
    case "diagnostics":
      return typeof candidate.sourceHash === "string" && Array.isArray(candidate.diagnostics) && candidate.diagnostics.every(isAnalysisDiagnostic);
    case "file-snapshot":
      return candidate.snapshot === null || isAnalysisFileSnapshot(candidate.snapshot);
    case "error":
      return typeof candidate.error === "string";
    default:
      return false;
  }
}
function computeFormSpecTextHash(text) {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}
function serializeCommentTargetSpecifier(target) {
  if (target === null) {
    return null;
  }
  return {
    rawText: target.rawText,
    valid: target.valid,
    kind: target.kind,
    fullSpan: target.fullSpan,
    colonSpan: target.colonSpan,
    span: target.span
  };
}
function serializeCommentTagSemanticContext(semantic) {
  return {
    tagName: semantic.tag.normalizedTagName,
    tagDefinition: semantic.tagDefinition === null ? null : {
      canonicalName: semantic.tagDefinition.canonicalName,
      completionDetail: semantic.tagDefinition.completionDetail,
      hoverMarkdown: semantic.tagDefinition.hoverMarkdown
    },
    placement: semantic.placement,
    contextualSignatures: semantic.contextualSignatures.map((signature) => ({
      label: signature.label,
      placements: signature.placements
    })),
    supportedTargets: semantic.supportedTargets,
    targetCompletions: semantic.targetCompletions,
    compatiblePathTargets: semantic.compatiblePathTargets,
    valueLabels: semantic.valueLabels,
    argumentCompletions: semantic.argumentCompletions,
    contextualTagHoverMarkdown: semantic.contextualTagHoverMarkdown,
    signatures: semantic.signatures.map((signature) => ({
      label: signature.label,
      placements: signature.placements
    })),
    tagHoverMarkdown: semantic.tagHoverMarkdown,
    targetHoverMarkdown: semantic.targetHoverMarkdown,
    argumentHoverMarkdown: semantic.argumentHoverMarkdown
  };
}
function serializeCompletionContext(context) {
  switch (context.kind) {
    case "tag-name":
      return {
        kind: "tag-name",
        prefix: context.prefix,
        availableTags: context.availableTags.map((tag) => ({
          canonicalName: tag.canonicalName,
          completionDetail: tag.completionDetail,
          hoverMarkdown: tag.hoverMarkdown
        }))
      };
    case "target":
      return {
        kind: "target",
        semantic: serializeCommentTagSemanticContext(context.semantic)
      };
    case "argument":
      return {
        kind: "argument",
        semantic: serializeCommentTagSemanticContext(context.semantic),
        valueLabels: context.valueLabels
      };
    case "none":
      return { kind: "none" };
    default: {
      const exhaustive = context;
      return exhaustive;
    }
  }
}
function serializeHoverInfo(hover) {
  return hover === null ? null : {
    kind: hover.kind,
    markdown: hover.markdown
  };
}
function serializeParsedCommentTag(tag, semantic) {
  return {
    rawTagName: tag.rawTagName,
    normalizedTagName: tag.normalizedTagName,
    recognized: tag.recognized,
    fullSpan: tag.fullSpan,
    tagNameSpan: tag.tagNameSpan,
    payloadSpan: tag.payloadSpan,
    target: serializeCommentTargetSpecifier(tag.target),
    argumentSpan: tag.argumentSpan,
    argumentText: tag.argumentText,
    semantic: serializeCommentTagSemanticContext(semantic)
  };
}

// src/workspace-runtime.ts
function getFormSpecWorkspaceId(workspaceRoot) {
  return computeFormSpecTextHash(workspaceRoot);
}
function getFormSpecWorkspaceRuntimeDirectory(workspaceRoot) {
  return path.join(workspaceRoot, ".cache", "formspec", "tooling");
}
function getFormSpecManifestPath(workspaceRoot) {
  return path.join(getFormSpecWorkspaceRuntimeDirectory(workspaceRoot), "manifest.json");
}

// src/file-snapshots.ts
import * as ts5 from "typescript";

// src/compiler-signatures.ts
import * as ts3 from "typescript";

// src/perf-tracing.ts
var EMPTY_PERFORMANCE_EVENTS = Object.freeze([]);
function getHighResolutionTime() {
  return performance.now();
}
var FormSpecPerformanceRecorderImpl = class {
  mutableEvents = [];
  get events() {
    return this.mutableEvents;
  }
  measure(name, detail, callback) {
    const startedAt = getHighResolutionTime();
    try {
      return callback();
    } finally {
      this.record({
        name,
        durationMs: getHighResolutionTime() - startedAt,
        ...detail === void 0 ? {} : { detail }
      });
    }
  }
  record(event) {
    this.mutableEvents.push(event);
  }
};
var NoopFormSpecPerformanceRecorderImpl = class {
  get events() {
    return EMPTY_PERFORMANCE_EVENTS;
  }
  measure(_name, _detail, callback) {
    return callback();
  }
  record(_event) {
  }
};
var NOOP_FORMSPEC_PERFORMANCE_RECORDER = new NoopFormSpecPerformanceRecorderImpl();
function createFormSpecPerformanceRecorder() {
  return new FormSpecPerformanceRecorderImpl();
}
function getFormSpecPerformanceNow() {
  return getHighResolutionTime();
}
function optionalMeasure(recorder, name, detail, callback) {
  return (recorder ?? NOOP_FORMSPEC_PERFORMANCE_RECORDER).measure(name, detail, callback);
}

// src/compiler-signatures.ts
var SYNTHETIC_CHECK_EVENT = {
  batch: "analysis.syntheticCheckBatch",
  narrowBatch: "analysis.narrowSyntheticCheckBatch"
};
var PRELUDE_LINES = [
  "type FormSpecPlacement =",
  '  | "class"',
  '  | "class-field"',
  '  | "class-method"',
  '  | "interface"',
  '  | "interface-field"',
  '  | "type-alias"',
  '  | "type-alias-field"',
  '  | "variable"',
  '  | "function"',
  '  | "function-parameter"',
  '  | "method-parameter";',
  "",
  "type FormSpecCapability =",
  '  | "numeric-comparable"',
  '  | "string-like"',
  '  | "array-like"',
  '  | "enum-member-addressable"',
  '  | "json-like"',
  '  | "condition-like"',
  '  | "object-like";',
  "",
  "interface TagContext<P extends FormSpecPlacement, Host, Subject> {",
  "  readonly placement: P;",
  "  readonly hostType: Host;",
  "  readonly subjectType: Subject;",
  "}",
  "",
  "type NonNullish<T> = Exclude<T, null | undefined>;",
  "",
  "type ProvidesCapability<T, Capability extends FormSpecCapability> =",
  '  Capability extends "numeric-comparable"',
  "    ? NonNullish<T> extends number | bigint",
  "      ? true",
  "      : false",
  '    : Capability extends "string-like"',
  "      ? NonNullish<T> extends string",
  "        ? true",
  "        : false",
  '      : Capability extends "array-like"',
  "        ? NonNullish<T> extends readonly unknown[]",
  "          ? true",
  "          : false",
  '        : Capability extends "enum-member-addressable"',
  "          ? NonNullish<T> extends string",
  "            ? true",
  "            : false",
  '          : Capability extends "json-like"',
  "            ? true",
  '            : Capability extends "condition-like"',
  "              ? true",
  '              : Capability extends "object-like"',
  "                ? NonNullish<T> extends readonly unknown[]",
  "                  ? false",
  "                  : NonNullish<T> extends object",
  "                    ? true",
  "                    : false",
  "                : false;",
  "",
  "type NestedPathOfCapability<Subject, Capability extends FormSpecCapability> =",
  "  NonNullish<Subject> extends readonly (infer Item)[]",
  "    ? NestedPathOfCapability<Item, Capability>",
  "    : NonNullish<Subject> extends object",
  "      ? {",
  "          [Key in Extract<keyof NonNullish<Subject>, string>]:",
  "            | (ProvidesCapability<NonNullish<Subject>[Key], Capability> extends true ? Key : never)",
  "            | (NestedPathOfCapability<NonNullish<Subject>[Key], Capability> extends never",
  "                ? never",
  "                : `${Key}.${NestedPathOfCapability<NonNullish<Subject>[Key], Capability>}`);",
  "        }[Extract<keyof NonNullish<Subject>, string>]",
  "      : never;",
  "",
  "type PathOfCapability<Subject, Capability extends FormSpecCapability> =",
  "  NestedPathOfCapability<Subject, Capability>;",
  "",
  "type MemberTarget<Subject> = Extract<keyof NonNullish<Subject>, string>;",
  "",
  'type VariantTarget<Subject> = "singular" | "plural";',
  "",
  "type FormSpecCondition = unknown;",
  "type JsonValue = unknown;",
  "",
  "declare function __ctx<P extends FormSpecPlacement, Host, Subject>(): TagContext<P, Host, Subject>;",
  "declare function __path<Subject, Capability extends FormSpecCapability>(",
  "  path: PathOfCapability<Subject, Capability>",
  "): PathOfCapability<Subject, Capability>;",
  "declare function __member<Subject>(member: MemberTarget<Subject>): MemberTarget<Subject>;",
  "declare function __variant<Subject>(variant: VariantTarget<Subject>): VariantTarget<Subject>;"
];
function placementUnion(placements) {
  return placements.map((placement) => JSON.stringify(placement)).join(" | ");
}
function renderValueType(valueKind) {
  switch (valueKind) {
    case "number":
    case "integer":
    case "signedInteger":
      return "number";
    case "string":
      return "string";
    case "json":
      return "JsonValue";
    case "boolean":
      return "boolean";
    case "condition":
      return "FormSpecCondition";
    case void 0:
      return "unknown";
    default: {
      const exhaustive = valueKind;
      return exhaustive;
    }
  }
}
function renderTargetParameterType(parameter) {
  switch (parameter.kind) {
    case "target-path":
      return parameter.capability === void 0 ? "PathOfCapability<Subject, FormSpecCapability>" : `PathOfCapability<Subject, ${JSON.stringify(parameter.capability)}>`;
    case "target-member":
      return "MemberTarget<Subject>";
    case "target-variant":
      return "VariantTarget<Subject>";
    case "value":
      return renderValueType(parameter.valueKind);
    default: {
      const exhaustive = parameter.kind;
      return exhaustive;
    }
  }
}
function renderSignature(tagName, signature) {
  const parameters = signature.parameters.map((parameter, index) => {
    const name = parameter.kind === "value" ? "value" : `target${String(index)}`;
    return `${name}: ${renderTargetParameterType(parameter)}`;
  });
  return [
    `  function ${getSyntheticTagHelperName(tagName)}<Host, Subject>(`,
    `    ctx: TagContext<${placementUnion(signature.placements)}, Host, Subject>${parameters.length > 0 ? "," : ""}`,
    ...parameters.map(
      (parameter, index) => `    ${parameter}${index === parameters.length - 1 ? "" : ","}`
    ),
    "  ): void;"
  ].join("\n");
}
function getSyntheticTagHelperName(tagName) {
  return `tag_${tagName}`;
}
function targetKindForParameter(parameter) {
  switch (parameter.kind) {
    case "target-path":
      return "path";
    case "target-member":
      return "member";
    case "target-variant":
      return "variant";
    case "value":
      return null;
    default: {
      const exhaustive = parameter.kind;
      return exhaustive;
    }
  }
}
function getSignatureTargetKind(signature) {
  for (const parameter of signature.parameters) {
    const targetKind = targetKindForParameter(parameter);
    if (targetKind !== null) {
      return targetKind;
    }
  }
  return null;
}
function getTargetParameter(signature) {
  return signature.parameters.find(
    (parameter) => parameter.kind !== "value"
  ) ?? null;
}
function getPathTargetCapability(signature) {
  const parameter = getTargetParameter(signature);
  if (parameter?.kind !== "target-path") {
    throw new Error(`Invariant violation: expected a path-target synthetic signature`);
  }
  if (parameter.capability === void 0) {
    throw new Error(
      `Invariant violation: path-target synthetic signatures must declare a capability`
    );
  }
  return JSON.stringify(parameter.capability);
}
function renderTargetArgument(target, signature, subjectType) {
  switch (target.kind) {
    case "path":
      return `__path<${subjectType}, ${getPathTargetCapability(signature)}>(${JSON.stringify(
        target.text
      )})`;
    case "member":
      return `__member<${subjectType}>(${JSON.stringify(target.text)})`;
    case "variant":
      return `__variant<${subjectType}>(${JSON.stringify(target.text)})`;
  }
}
function getMatchingTagSignatures(definition, placement, targetKind) {
  return definition.signatures.filter(
    (signature) => signature.placements.includes(placement) && getSignatureTargetKind(signature) === targetKind
  );
}
var TS_PRIMITIVE_KEYWORDS = /* @__PURE__ */ new Set([
  "any",
  "bigint",
  "boolean",
  "never",
  "null",
  "number",
  "object",
  "string",
  "symbol",
  "undefined",
  "unknown",
  "void"
]);
var TS_GLOBAL_BUILTIN_TYPES = /* @__PURE__ */ new Map([
  ["Date", true],
  // ISO 8601 datetime -- { type: "string", format: "date-time" }
  ["Array", false],
  ["ArrayBuffer", false],
  ["BigInt", false],
  ["Boolean", false],
  ["DataView", false],
  ["Error", false],
  ["EvalError", false],
  ["Float32Array", false],
  ["Float64Array", false],
  ["Function", false],
  ["Int16Array", false],
  ["Int32Array", false],
  ["Int8Array", false],
  ["Map", false],
  ["Number", false],
  ["Object", false],
  ["Promise", false],
  ["Proxy", false],
  ["RangeError", false],
  ["ReferenceError", false],
  ["RegExp", false],
  ["Set", false],
  ["SharedArrayBuffer", false],
  ["String", false],
  ["Symbol", false],
  ["SyntaxError", false],
  ["TypeError", false],
  ["URIError", false],
  ["Uint16Array", false],
  ["Uint32Array", false],
  ["Uint8Array", false],
  ["Uint8ClampedArray", false],
  ["WeakMap", false],
  ["WeakSet", false]
]);
function collectExtensionCustomTypeNames(extensions) {
  if (extensions === void 0) {
    return [];
  }
  const seen = /* @__PURE__ */ new Map();
  const result = [];
  for (const ext of extensions) {
    for (const customType of ext.customTypes ?? []) {
      for (const tsName of customType.tsTypeNames) {
        if (TS_PRIMITIVE_KEYWORDS.has(tsName)) {
          continue;
        }
        const globalBuiltinSupported = TS_GLOBAL_BUILTIN_TYPES.get(tsName);
        if (globalBuiltinSupported === true) {
          continue;
        }
        if (globalBuiltinSupported === false) {
          throw createSyntheticSetupError(
            "unsupported-custom-type-override",
            `Custom type name "${tsName}" registered by extension "${ext.extensionId}" conflicts with a TypeScript global built-in type that FormSpec does not yet support overriding. Rename the custom type to a non-conflicting name.`
          );
        }
        if (!/^[$_a-zA-Z][$_a-zA-Z0-9]*$/.test(tsName)) {
          throw createSyntheticSetupError(
            "synthetic-setup",
            `Invalid custom type name "${tsName}" registered by extension "${ext.extensionId}": must be a valid TypeScript identifier.`
          );
        }
        const existingExtensionId = seen.get(tsName);
        if (existingExtensionId !== void 0) {
          throw createSyntheticSetupError(
            "synthetic-setup",
            `Duplicate custom type name "${tsName}" registered by extensions "${existingExtensionId}" and "${ext.extensionId}". Extension-registered types must have unique names.`
          );
        }
        seen.set(tsName, ext.extensionId);
        result.push(tsName);
      }
    }
  }
  return result;
}
function buildSyntheticHelperPrelude(extensions) {
  const lines = [...PRELUDE_LINES];
  const customTypeNames = collectExtensionCustomTypeNames(extensions);
  if (customTypeNames.length > 0) {
    lines.push("");
    lines.push("// Extension-registered custom types");
    for (const tsName of customTypeNames) {
      lines.push(`type ${tsName} = unknown;`);
    }
  }
  lines.push("", "declare namespace __formspec {");
  for (const definition of getAllTagDefinitions(extensions)) {
    for (const signature of definition.signatures) {
      lines.push(renderSignature(definition.canonicalName, signature));
    }
  }
  lines.push("}");
  return lines.join("\n");
}
function lowerTagApplicationToSyntheticCall(options) {
  const definition = getTagDefinition(options.tagName, options.extensions);
  if (definition === null) {
    throw new Error(`Unknown FormSpec tag: ${options.tagName}`);
  }
  const targetKind = options.target?.kind ?? null;
  const matchingSignatures = getMatchingTagSignatures(definition, options.placement, targetKind);
  if (matchingSignatures.length === 0) {
    throw new Error(
      `No synthetic signature for @${definition.canonicalName} on placement "${options.placement}"` + (targetKind === null ? "" : ` with target kind "${targetKind}"`)
    );
  }
  const args = [
    `__ctx<${JSON.stringify(options.placement)}, ${options.hostType}, ${options.subjectType}>()`
  ];
  const signature = matchingSignatures[0];
  if (signature === void 0) {
    throw new Error(
      `Invariant violation: missing synthetic signature for @${definition.canonicalName}`
    );
  }
  if (options.target !== void 0 && options.target !== null) {
    args.push(renderTargetArgument(options.target, signature, options.subjectType));
  }
  if (options.argumentExpression !== void 0 && options.argumentExpression !== null) {
    args.push(options.argumentExpression);
  }
  return {
    definition,
    matchingSignatures,
    callExpression: `__formspec.${getSyntheticTagHelperName(definition.canonicalName)}(${args.join(", ")});`
  };
}
function createSyntheticCompilerHost(fileName, sourceText, compilerOptions) {
  const host = ts3.createCompilerHost(compilerOptions, true);
  const originalGetSourceFile = host.getSourceFile.bind(host);
  host.getSourceFile = (requestedFileName, languageVersion, onError, shouldCreateNewSourceFile) => {
    if (requestedFileName === fileName) {
      return ts3.createSourceFile(requestedFileName, sourceText, languageVersion, true);
    }
    return originalGetSourceFile(
      requestedFileName,
      languageVersion,
      onError,
      shouldCreateNewSourceFile
    );
  };
  host.readFile = (requestedFileName) => {
    if (requestedFileName === fileName) {
      return sourceText;
    }
    return ts3.sys.readFile(requestedFileName);
  };
  host.fileExists = (requestedFileName) => requestedFileName === fileName || ts3.sys.fileExists(requestedFileName);
  host.writeFile = () => void 0;
  return host;
}
function flattenDiagnosticMessage(message) {
  return ts3.flattenDiagnosticMessageText(message, "\n");
}
function createSyntheticSetupError(diagnosticKind, message) {
  const error = new Error(message);
  error.diagnosticKind = diagnosticKind;
  return error;
}
function isSyntheticSetupError(error) {
  return error instanceof Error && "diagnosticKind" in error;
}
function isUnsupportedCustomTypeOverrideErrorMessage(message) {
  return message.includes("conflicts with a TypeScript global built-in type");
}
var SYNTHETIC_COMPILER_OPTIONS = {
  strict: true,
  noEmit: true,
  target: ts3.ScriptTarget.ES2022,
  module: ts3.ModuleKind.ESNext,
  lib: ["lib.es2022.d.ts"],
  types: []
};
var syntheticBatchResultCache = new LruCache(
  FORM_SPEC_SYNTHETIC_BATCH_CACHE_ENTRIES
);
function getEffectiveSyntheticCompilerOptions(compilerOptionsOverrides) {
  return compilerOptionsOverrides !== void 0 ? { ...SYNTHETIC_COMPILER_OPTIONS, ...compilerOptionsOverrides } : SYNTHETIC_COMPILER_OPTIONS;
}
function stableSerializeCacheValue(value) {
  if (value === void 0) {
    return "undefined";
  }
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableSerializeCacheValue(entry)).join(",")}]`;
  }
  const entries = Object.entries(value).filter(([, entry]) => entry !== void 0).sort(([left], [right]) => left.localeCompare(right));
  return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stableSerializeCacheValue(entry)}`).join(",")}}`;
}
function buildSyntheticBatchCacheKey(sourceText, compilerOptionsOverrides) {
  return `${stableSerializeCacheValue(getEffectiveSyntheticCompilerOptions(compilerOptionsOverrides))}
${sourceText}`;
}
function pushChunkLines(target, chunk) {
  target.push(...chunk.split("\n"));
}
function buildSyntheticBatchSource(applications) {
  const lines = [];
  pushChunkLines(lines, buildSyntheticHelperPrelude(collectBatchExtensions(applications)));
  lines.push("");
  const applicationLineRanges = [];
  for (const [index, application] of applications.entries()) {
    const namespaceName = `__formspec_app_${String(index)}`;
    const startLine = lines.length;
    lines.push(`namespace ${namespaceName} {`);
    for (const declaration of application.options.supportingDeclarations ?? []) {
      pushChunkLines(lines, declaration);
    }
    lines.push(`type __Host = ${application.options.hostType};`);
    lines.push(`type __Subject = ${application.options.subjectType};`);
    lines.push(application.lowered.callExpression);
    lines.push("}");
    applicationLineRanges.push({
      startLine,
      endLine: lines.length - 1
    });
  }
  return {
    sourceText: lines.join("\n"),
    applicationLineRanges
  };
}
function collectBatchExtensions(applications) {
  const seen = /* @__PURE__ */ new Set();
  const extensions = [];
  for (const application of applications) {
    for (const ext of application.options.extensions ?? []) {
      if (!seen.has(ext.extensionId)) {
        seen.add(ext.extensionId);
        extensions.push(ext);
      }
    }
  }
  return extensions.length === 0 ? void 0 : extensions;
}
function mapBatchDiagnostics(diagnostics, sourceFile, applicationLineRanges) {
  const diagnosticsByApplication = applicationLineRanges.map(
    () => []
  );
  const globalDiagnostics = [];
  for (const diagnostic of diagnostics) {
    const serialized = {
      kind: "typescript",
      code: diagnostic.code,
      message: flattenDiagnosticMessage(diagnostic.messageText)
    };
    if (diagnostic.file === void 0 || diagnostic.start === void 0) {
      globalDiagnostics.push(serialized);
      continue;
    }
    const line = diagnostic.file.getLineAndCharacterOfPosition(diagnostic.start).line;
    const applicationIndex = applicationLineRanges.findIndex(
      (range) => line >= range.startLine && line <= range.endLine
    );
    if (applicationIndex < 0) {
      globalDiagnostics.push(serialized);
      continue;
    }
    const targetResult = diagnosticsByApplication[applicationIndex];
    if (targetResult !== void 0) {
      targetResult.push(serialized);
    }
  }
  return {
    applicationResults: diagnosticsByApplication.map((diagnosticsForApplication) => ({
      sourceText: sourceFile.text,
      diagnostics: diagnosticsForApplication
    })),
    globalDiagnostics
  };
}
function runSyntheticProgram(fileName, sourceText, performance2, eventPrefix, missingSourceFileMessage, compilerOptionsOverrides) {
  const effectiveOptions = getEffectiveSyntheticCompilerOptions(compilerOptionsOverrides);
  const host = optionalMeasure(
    performance2,
    `${eventPrefix}.createCompilerHost`,
    void 0,
    () => createSyntheticCompilerHost(fileName, sourceText, effectiveOptions)
  );
  const program = optionalMeasure(
    performance2,
    `${eventPrefix}.createProgram`,
    void 0,
    () => ts3.createProgram([fileName], effectiveOptions, host)
  );
  const diagnostics = optionalMeasure(
    performance2,
    `${eventPrefix}.getPreEmitDiagnostics`,
    void 0,
    () => ts3.getPreEmitDiagnostics(program).filter(
      (diagnostic) => diagnostic.file === void 0 || diagnostic.file.fileName === fileName
    )
  );
  const sourceFile = program.getSourceFile(fileName);
  if (sourceFile === void 0) {
    throw new Error(missingSourceFileMessage);
  }
  return {
    sourceFile,
    diagnostics
  };
}
function createEmptySyntheticTagCheckResults(applicationCount, sourceText) {
  return Array.from({ length: applicationCount }, () => ({
    sourceText,
    diagnostics: []
  }));
}
function serializeSyntheticBatchError(error) {
  const message = error instanceof Error ? error.message : String(error);
  return {
    kind: isSyntheticSetupError(error) ? error.diagnosticKind : isUnsupportedCustomTypeOverrideErrorMessage(message) ? "unsupported-custom-type-override" : "synthetic-setup",
    // Negative codes are reserved for FormSpec-generated synthetic setup errors.
    code: -1,
    message
  };
}
function runBatchSyntheticCheck(options) {
  if (options.applications.length === 0) {
    return {
      sourceText: "",
      applicationResults: [],
      globalDiagnostics: []
    };
  }
  const resolvedApplications = options.lowerApplications(options.applications, options.performance);
  let batchSource;
  try {
    batchSource = options.buildBatchSource(resolvedApplications, options.performance);
  } catch (error) {
    return {
      sourceText: "",
      applicationResults: createEmptySyntheticTagCheckResults(options.applications.length, ""),
      globalDiagnostics: [serializeSyntheticBatchError(error)]
    };
  }
  const cacheKey = buildSyntheticBatchCacheKey(batchSource.sourceText, options.compilerOptions);
  const cached = options.cache.get(cacheKey);
  if (cached !== void 0) {
    options.performance?.record({
      name: `${options.eventPrefix}.cacheHit`,
      durationMs: 0,
      detail: {
        applicationCount: options.applications.length
      }
    });
    return {
      sourceText: batchSource.sourceText,
      applicationResults: cached.applicationResults,
      globalDiagnostics: cached.globalDiagnostics
    };
  }
  options.performance?.record({
    name: `${options.eventPrefix}.cacheMiss`,
    durationMs: 0,
    detail: {
      applicationCount: options.applications.length
    }
  });
  const { sourceFile, diagnostics } = runSyntheticProgram(
    options.fileName,
    batchSource.sourceText,
    options.performance,
    options.eventPrefix,
    options.missingSourceFileMessage,
    options.compilerOptions
  );
  const results = optionalMeasure(
    options.performance,
    `${options.eventPrefix}.mapDiagnostics`,
    void 0,
    () => mapBatchDiagnostics(diagnostics, sourceFile, batchSource.applicationLineRanges)
  );
  options.cache.set(cacheKey, results);
  return {
    sourceText: batchSource.sourceText,
    applicationResults: results.applicationResults,
    globalDiagnostics: results.globalDiagnostics
  };
}
function checkSyntheticTagApplicationsDetailed(options) {
  return runBatchSyntheticCheck({
    applications: options.applications,
    performance: options.performance,
    compilerOptions: options.compilerOptions,
    cache: syntheticBatchResultCache,
    eventPrefix: SYNTHETIC_CHECK_EVENT.batch,
    missingSourceFileMessage: "Invariant violation: missing synthetic batch source file",
    fileName: "/virtual/formspec-synthetic-batch.ts",
    lowerApplications: (applications, performance2) => optionalMeasure(
      performance2,
      `${SYNTHETIC_CHECK_EVENT.batch}.lower`,
      void 0,
      () => applications.map((application) => ({
        options: application,
        lowered: lowerTagApplicationToSyntheticCall({
          ...application,
          hostType: "__Host",
          subjectType: "__Subject"
        })
      }))
    ),
    buildBatchSource: (applications, performance2) => optionalMeasure(
      performance2,
      `${SYNTHETIC_CHECK_EVENT.batch}.buildSource`,
      void 0,
      () => buildSyntheticBatchSource(applications)
    )
  });
}
function checkSyntheticTagApplications(options) {
  const result = checkSyntheticTagApplicationsDetailed(options);
  if (result.globalDiagnostics.length === 0) {
    return result.applicationResults;
  }
  return result.applicationResults.map((applicationResult) => ({
    sourceText: applicationResult.sourceText,
    diagnostics: [...applicationResult.diagnostics, ...result.globalDiagnostics]
  }));
}
function checkSyntheticTagApplication(options) {
  const batchResult = checkSyntheticTagApplicationsDetailed({
    applications: [options],
    ...options.performance === void 0 ? {} : { performance: options.performance },
    ...options.compilerOptions === void 0 ? {} : { compilerOptions: options.compilerOptions }
  });
  const result = batchResult.applicationResults[0];
  if (result === void 0) {
    throw new Error("Invariant violation: missing synthetic batch result for singular check");
  }
  if (batchResult.globalDiagnostics.length === 0) {
    return result;
  }
  return {
    sourceText: result.sourceText,
    diagnostics: [...result.diagnostics, ...batchResult.globalDiagnostics]
  };
}

// src/metadata-analysis.ts
import * as ts4 from "typescript";
var BUILTIN_METADATA_TAG_NAMES = /* @__PURE__ */ new Set(["apiName", "displayName"]);
function validateExtensionMetadataSlots(extensions) {
  const slotIds = /* @__PURE__ */ new Set();
  const tagNames = new Set(BUILTIN_METADATA_TAG_NAMES);
  const metadataFreeExtensions = extensions?.map((extension) => ({
    extensionId: extension.extensionId,
    ...extension.constraintTags !== void 0 ? {
      constraintTags: extension.constraintTags.map((tag) => ({
        tagName: normalizeFormSpecTagName(tag.tagName)
      }))
    } : {}
  })) ?? [];
  for (const extension of extensions ?? []) {
    for (const slot of extension.metadataSlots ?? []) {
      if (slotIds.has(slot.slotId)) {
        throw new Error(`Duplicate metadata slot ID: "${slot.slotId}"`);
      }
      slotIds.add(slot.slotId);
      const canonicalTagName = normalizeFormSpecTagName(slot.tagName);
      if (tagNames.has(canonicalTagName)) {
        throw BUILTIN_METADATA_TAG_NAMES.has(canonicalTagName) ? new Error(
          `Metadata tag "@${canonicalTagName}" conflicts with built-in metadata tags.`
        ) : new Error(`Duplicate metadata tag: "@${canonicalTagName}"`);
      }
      const existingTag = getTagDefinition(canonicalTagName, metadataFreeExtensions);
      if (existingTag !== null) {
        throw BUILTIN_METADATA_TAG_NAMES.has(existingTag.canonicalName) ? new Error(
          `Metadata tag "@${canonicalTagName}" conflicts with built-in metadata tags.`
        ) : new Error(
          `Metadata tag "@${canonicalTagName}" conflicts with existing FormSpec tag "@${existingTag.canonicalName}".`
        );
      }
      tagNames.add(canonicalTagName);
    }
  }
}
function getLogicalName(node) {
  if ((ts4.isClassDeclaration(node) || ts4.isInterfaceDeclaration(node) || ts4.isTypeAliasDeclaration(node) || ts4.isFunctionDeclaration(node) || ts4.isMethodDeclaration(node) || ts4.isVariableDeclaration(node)) && node.name !== void 0) {
    if (ts4.isIdentifier(node.name)) {
      return node.name.text;
    }
    if (ts4.isStringLiteralLike(node.name) || ts4.isNumericLiteral(node.name)) {
      return node.name.text;
    }
  }
  if (ts4.isPropertyDeclaration(node) || ts4.isPropertySignature(node) || ts4.isParameter(node)) {
    if (ts4.isIdentifier(node.name)) {
      return node.name.text;
    }
    if (ts4.isStringLiteralLike(node.name) || ts4.isNumericLiteral(node.name)) {
      return node.name.text;
    }
  }
  return null;
}
function getMetadataDeclarationKind(node) {
  const placement = resolveDeclarationPlacement(node);
  switch (placement) {
    case "class":
    case "interface":
    case "type-alias":
      return "type";
    case "class-field":
    case "interface-field":
    case "type-alias-field":
    case "variable":
    case "function-parameter":
    case "method-parameter":
      return "field";
    case "class-method":
    case "function":
      return "method";
    default:
      return null;
  }
}
function toResolvedScalar(entry) {
  return entry === void 0 ? void 0 : { value: entry.value, source: entry.source };
}
function toBuiltInResolvedMetadata(entries) {
  const apiName = toResolvedScalar(
    entries.find((entry) => entry.slotId === "apiName" && entry.qualifier === void 0)
  );
  const displayName = toResolvedScalar(
    entries.find((entry) => entry.slotId === "displayName" && entry.qualifier === void 0)
  );
  const apiNamePlural = toResolvedScalar(
    entries.find((entry) => entry.slotId === "apiName" && entry.qualifier === "plural")
  );
  const displayNamePlural = toResolvedScalar(
    entries.find((entry) => entry.slotId === "displayName" && entry.qualifier === "plural")
  );
  if (apiName === void 0 && displayName === void 0 && apiNamePlural === void 0 && displayNamePlural === void 0) {
    return void 0;
  }
  return {
    ...apiName !== void 0 && { apiName },
    ...displayName !== void 0 && { displayName },
    ...apiNamePlural !== void 0 && { apiNamePlural },
    ...displayNamePlural !== void 0 && { displayNamePlural }
  };
}
function normalizeBuiltInSlots(metadata) {
  const declarationKinds = ["type", "field", "method"];
  const qualifierNames = ["singular", "plural"];
  const builtInPolicyByKind = {
    type: metadata?.type,
    field: metadata?.field,
    method: metadata?.method
  };
  const createSlot = (slotId, tagName) => ({
    slotId,
    tagName,
    declarationKinds,
    allowBare: true,
    primaryQualifierAliases: ["singular"],
    inferValue: (context) => {
      const input = builtInPolicyByKind[context.declarationKind]?.[slotId];
      return input?.mode === "infer-if-missing" ? input.infer(context) : "";
    },
    qualifiers: qualifierNames.map((qualifier) => ({
      qualifier,
      ...qualifier === "plural" ? {
        sourceQualifier: "singular",
        inferValue: (context) => {
          const input = builtInPolicyByKind[context.declarationKind]?.[slotId];
          if (input?.pluralization?.mode !== "infer-if-missing" || context.baseValue === void 0) {
            return "";
          }
          return input.pluralization.inflect({
            surface: context.surface,
            declarationKind: context.declarationKind,
            logicalName: context.logicalName,
            singular: context.baseValue,
            ...context.buildContext !== void 0 && { buildContext: context.buildContext }
          });
        }
      } : {}
    }))
  });
  return [createSlot("apiName", "apiName"), createSlot("displayName", "displayName")];
}
function normalizeExtensionSlots(extensions) {
  const seenSlotIds = /* @__PURE__ */ new Set();
  const seenTagNames = /* @__PURE__ */ new Set();
  return extensions?.flatMap(
    (extension) => (extension.metadataSlots ?? []).map((slot) => {
      const normalizedTagName = normalizeFormSpecTagName(slot.tagName);
      if (seenSlotIds.has(slot.slotId)) {
        throw new Error(`Duplicate metadata slot ID: "${slot.slotId}"`);
      }
      seenSlotIds.add(slot.slotId);
      if (seenTagNames.has(normalizedTagName)) {
        throw new Error(`Duplicate metadata tag: "@${normalizedTagName}"`);
      }
      seenTagNames.add(normalizedTagName);
      return {
        slotId: slot.slotId,
        tagName: normalizedTagName,
        declarationKinds: slot.declarationKinds,
        allowBare: slot.allowBare !== false,
        // Extension slots only accept the qualifiers they explicitly register.
        primaryQualifierAliases: [],
        ...slot.inferValue !== void 0 && { inferValue: slot.inferValue },
        ...slot.isApplicable !== void 0 && { isApplicable: slot.isApplicable },
        qualifiers: slot.qualifiers?.map((qualifier) => ({
          qualifier: qualifier.qualifier,
          ...qualifier.sourceQualifier !== void 0 && {
            sourceQualifier: qualifier.sourceQualifier
          },
          ...qualifier.inferValue !== void 0 && { inferValue: qualifier.inferValue }
        })) ?? []
      };
    })
  ) ?? [];
}
function getApplicableSlots(node, declarationKind, logicalName, metadata, extensions, buildContext) {
  validateExtensionMetadataSlots(extensions);
  return [...normalizeBuiltInSlots(metadata), ...normalizeExtensionSlots(extensions)].filter(
    (slot) => slot.declarationKinds.includes(declarationKind) && (slot.isApplicable?.({
      surface: "tsdoc",
      declarationKind,
      logicalName,
      buildContext: {
        node,
        ...buildContext !== void 0 && typeof buildContext === "object" ? buildContext : {}
      }
    }) ?? true)
  );
}
function toApplicableSlot(slot) {
  return {
    slotId: slot.slotId,
    tagName: slot.tagName,
    allowBare: slot.allowBare,
    qualifiers: slot.qualifiers.map((qualifier) => qualifier.qualifier)
  };
}
function buildExplicitSource(tag) {
  if (tag.argumentSpan === null) {
    throw new Error(
      `Expected explicit metadata tag @${tag.normalizedTagName} to have a value span.`
    );
  }
  return {
    tagName: tag.normalizedTagName,
    form: tag.target === null ? "bare" : "qualified",
    fullRange: { ...tag.fullSpan },
    tagNameRange: { ...tag.tagNameSpan },
    ...tag.target !== null && {
      qualifier: tag.target.rawText,
      qualifierRange: { ...tag.target.span }
    },
    valueRange: { ...tag.argumentSpan }
  };
}
function collectExplicitCandidates(node, slots, extensions) {
  const primaryEntries = /* @__PURE__ */ new Map();
  const qualifiedEntries = /* @__PURE__ */ new Map();
  const slotsByTagName = new Map(slots.map((slot) => [slot.tagName, slot]));
  const sourceFile = node.getSourceFile();
  const commentRanges = ts4.getLeadingCommentRanges(sourceFile.text, node.getFullStart()) ?? [];
  for (const range of commentRanges) {
    if (range.kind !== ts4.SyntaxKind.MultiLineCommentTrivia) {
      continue;
    }
    const commentText = sourceFile.text.slice(range.pos, range.end);
    if (!commentText.startsWith("/**")) {
      continue;
    }
    const parsed = parseCommentBlock(commentText, {
      offset: range.pos,
      extensions: extensions?.map((extension) => ({
        extensionId: extension.extensionId,
        ...extension.constraintTags !== void 0 ? {
          constraintTags: extension.constraintTags.map((tag) => ({ tagName: tag.tagName }))
        } : {},
        ...extension.metadataSlots !== void 0 ? {
          metadataSlots: extension.metadataSlots
        } : {}
      })) ?? []
    });
    for (const tag of parsed.tags) {
      const slot = slotsByTagName.get(tag.normalizedTagName);
      const value = tag.argumentText.trim();
      if (slot === void 0 || value === "") {
        continue;
      }
      const qualifier = tag.target?.rawText;
      if (qualifier === void 0) {
        if (slot.allowBare && !primaryEntries.has(slot.slotId)) {
          primaryEntries.set(slot.slotId, {
            value,
            explicitSource: buildExplicitSource(tag)
          });
        }
        continue;
      }
      if (slot.primaryQualifierAliases.includes(qualifier)) {
        if (!primaryEntries.has(slot.slotId)) {
          primaryEntries.set(slot.slotId, {
            value,
            qualifier,
            explicitSource: buildExplicitSource(tag)
          });
        }
        continue;
      }
      if (!slot.qualifiers.some((candidate) => candidate.qualifier === qualifier)) {
        continue;
      }
      const key = `${slot.slotId}:${qualifier}`;
      if (!qualifiedEntries.has(key)) {
        qualifiedEntries.set(key, {
          value,
          qualifier,
          explicitSource: buildExplicitSource(tag)
        });
      }
    }
  }
  return { primaryEntries, qualifiedEntries };
}
function inferEntry(slot, declarationKind, logicalName, qualifier, buildContext, inferValue, baseValue) {
  if (inferValue === void 0) {
    return null;
  }
  const value = inferValue({
    surface: "tsdoc",
    declarationKind,
    logicalName,
    slotId: slot.slotId,
    tagName: slot.tagName,
    ...qualifier !== void 0 && { qualifier },
    ...baseValue !== void 0 && { baseValue },
    ...buildContext !== void 0 && { buildContext }
  }).trim();
  return value === "" ? null : {
    slotId: slot.slotId,
    tagName: slot.tagName,
    ...qualifier !== void 0 && { qualifier },
    value,
    source: "inferred"
  };
}
function resolveSlotEntries(slot, declarationKind, logicalName, explicit, buildContext) {
  const entries = [];
  const primaryExplicit = explicit.primaryEntries.get(slot.slotId);
  const primaryEntry = primaryExplicit !== void 0 ? {
    slotId: slot.slotId,
    tagName: slot.tagName,
    value: primaryExplicit.value,
    source: "explicit",
    explicitSource: primaryExplicit.explicitSource
  } : inferEntry(
    slot,
    declarationKind,
    logicalName,
    void 0,
    buildContext,
    slot.inferValue,
    void 0
  );
  if (primaryEntry !== null) {
    entries.push(primaryEntry);
  }
  for (const qualifier of slot.qualifiers) {
    if (slot.primaryQualifierAliases.includes(qualifier.qualifier)) {
      continue;
    }
    const explicitKey = `${slot.slotId}:${qualifier.qualifier}`;
    const explicitQualified = explicit.qualifiedEntries.get(explicitKey);
    if (explicitQualified !== void 0) {
      entries.push({
        slotId: slot.slotId,
        tagName: slot.tagName,
        qualifier: qualifier.qualifier,
        value: explicitQualified.value,
        source: "explicit",
        explicitSource: explicitQualified.explicitSource
      });
      continue;
    }
    const sourceEntry = qualifier.sourceQualifier === void 0 ? primaryEntry : entries.find(
      (entry) => entry.slotId === slot.slotId && entry.qualifier === qualifier.sourceQualifier
    ) ?? (slot.primaryQualifierAliases.includes(qualifier.sourceQualifier) || slot.allowBare ? primaryEntry : void 0);
    const inferred = inferEntry(
      slot,
      declarationKind,
      logicalName,
      qualifier.qualifier,
      buildContext,
      qualifier.inferValue,
      sourceEntry?.value
    );
    if (inferred !== null) {
      entries.push(inferred);
    }
  }
  return entries;
}
function analyzeMetadataForNodeWithChecker(options) {
  const declarationKind = getMetadataDeclarationKind(options.node);
  const logicalName = options.logicalName ?? getLogicalName(options.node);
  if (declarationKind === null || logicalName === null) {
    return null;
  }
  const buildContext = {
    ...options.buildContext !== void 0 && typeof options.buildContext === "object" ? options.buildContext : {},
    ...options.program !== void 0 && { program: options.program },
    checker: options.checker,
    node: options.node,
    sourceFile: options.node.getSourceFile()
  };
  const applicableSlots = getApplicableSlots(
    options.node,
    declarationKind,
    logicalName,
    options.metadata,
    options.extensions,
    buildContext
  );
  const explicit = collectExplicitCandidates(options.node, applicableSlots, options.extensions);
  const entries = applicableSlots.flatMap(
    (slot) => resolveSlotEntries(slot, declarationKind, logicalName, explicit, buildContext)
  );
  return {
    declarationKind,
    logicalName,
    applicableSlots: applicableSlots.map((slot) => toApplicableSlot(slot)),
    entries,
    resolvedMetadata: toBuiltInResolvedMetadata(entries)
  };
}
function analyzeMetadataForNode(options) {
  return analyzeMetadataForNodeWithChecker({
    program: options.program,
    checker: options.program.getTypeChecker(),
    node: options.node,
    ...options.metadata !== void 0 ? { metadata: options.metadata } : {},
    ...options.extensions !== void 0 ? { extensions: options.extensions } : {}
  });
}
function analyzeMetadataForSourceFile(options) {
  const results = [];
  const checker = options.program.getTypeChecker();
  const visit = (node) => {
    const declarationKind = getMetadataDeclarationKind(node);
    if (declarationKind !== null && !ts4.isVariableDeclaration(node) && !ts4.isParameter(node)) {
      const analyzed = analyzeMetadataForNodeWithChecker({
        program: options.program,
        checker,
        node,
        ...options.metadata !== void 0 ? { metadata: options.metadata } : {},
        ...options.extensions !== void 0 ? { extensions: options.extensions } : {}
      });
      if (analyzed !== null) {
        results.push(analyzed);
      }
    }
    ts4.forEachChild(node, visit);
  };
  visit(options.sourceFile);
  return results;
}

// src/file-snapshots.ts
var SYNTHETIC_TYPE_NODE_BUILDER_FLAGS = ts5.NodeBuilderFlags.NoTruncation | ts5.NodeBuilderFlags.UseStructuralFallback | ts5.NodeBuilderFlags.IgnoreErrors | ts5.NodeBuilderFlags.InTypeAlias;
var SYNTHETIC_TYPE_PRINT_SOURCE_FILE = ts5.createSourceFile(
  "/virtual/formspec-standalone-type.ts",
  "",
  ts5.ScriptTarget.ES2022,
  false,
  ts5.ScriptKind.TS
);
var SYNTHETIC_TYPE_PRINTER = ts5.createPrinter({ removeComments: true });
function toExtensionTagSources(extensionDefinitions) {
  if (extensionDefinitions === void 0 || extensionDefinitions.length === 0) {
    return void 0;
  }
  return extensionDefinitions.map((extension) => ({
    extensionId: extension.extensionId,
    ...extension.constraintTags === void 0 ? {} : {
      constraintTags: extension.constraintTags.map((tag) => ({
        tagName: normalizeFormSpecTagName(tag.tagName)
      }))
    },
    ...extension.metadataSlots === void 0 ? {} : { metadataSlots: extension.metadataSlots },
    ...extension.types === void 0 ? {} : {
      customTypes: extension.types.map((type) => ({
        tsTypeNames: type.tsTypeNames ?? [type.typeName]
      }))
    }
  }));
}
function createConstraintTagRegistry(extensionDefinitions) {
  if (extensionDefinitions === void 0 || extensionDefinitions.length === 0) {
    return void 0;
  }
  return {
    extensions: extensionDefinitions,
    findConstraint(constraintId) {
      for (const extension of extensionDefinitions) {
        for (const constraint of extension.constraints ?? []) {
          if (`${extension.extensionId}/${constraint.constraintName}` === constraintId) {
            return constraint;
          }
        }
      }
      return void 0;
    },
    findConstraintTag(tagName) {
      const normalizedTagName = normalizeFormSpecTagName(tagName);
      for (const extension of extensionDefinitions) {
        for (const registration of extension.constraintTags ?? []) {
          if (normalizeFormSpecTagName(registration.tagName) === normalizedTagName) {
            return {
              extensionId: extension.extensionId,
              registration
            };
          }
        }
      }
      return void 0;
    },
    findBuiltinConstraintBroadening(typeId, tagName) {
      const normalizedTagName = normalizeFormSpecTagName(tagName);
      for (const extension of extensionDefinitions) {
        for (const type of extension.types ?? []) {
          if (type.typeName !== typeId) {
            continue;
          }
          for (const registration of type.builtinConstraintBroadenings ?? []) {
            if (normalizeFormSpecTagName(registration.tagName) === normalizedTagName) {
              return {
                extensionId: extension.extensionId,
                registration
              };
            }
          }
        }
      }
      return void 0;
    }
  };
}
function renderTargetLabel(targetPath) {
  return targetPath === null ? "Declaration" : `Target \`${targetPath}\``;
}
function spanFromPos(start, end) {
  return { start, end };
}
function toSerializedResolvedScalarMetadata(value) {
  return value === void 0 ? void 0 : { value: value.value, source: value.source };
}
function toSerializedResolvedMetadata(value) {
  if (value === void 0) {
    return null;
  }
  return {
    ...value.apiName === void 0 ? {} : { apiName: toSerializedResolvedScalarMetadata(value.apiName) },
    ...value.displayName === void 0 ? {} : { displayName: toSerializedResolvedScalarMetadata(value.displayName) },
    ...value.apiNamePlural === void 0 ? {} : { apiNamePlural: toSerializedResolvedScalarMetadata(value.apiNamePlural) },
    ...value.displayNamePlural === void 0 ? {} : { displayNamePlural: toSerializedResolvedScalarMetadata(value.displayNamePlural) }
  };
}
function toSerializedMetadataEntries(entries) {
  return entries.map((entry) => ({
    slotId: entry.slotId,
    tagName: entry.tagName,
    ...entry.qualifier === void 0 ? {} : { qualifier: entry.qualifier },
    value: entry.value,
    source: entry.source,
    ...entry.explicitSource === void 0 ? {} : {
      explicitSource: {
        tagName: entry.explicitSource.tagName,
        form: entry.explicitSource.form,
        fullRange: entry.explicitSource.fullRange,
        tagNameRange: entry.explicitSource.tagNameRange,
        ...entry.explicitSource.qualifierRange === void 0 ? {} : { qualifierRange: entry.explicitSource.qualifierRange },
        valueRange: entry.explicitSource.valueRange,
        ...entry.explicitSource.qualifier === void 0 ? {} : { qualifier: entry.explicitSource.qualifier }
      }
    }
  }));
}
function getTagPayloadText(parsed, tag) {
  if (tag.payloadSpan === null) {
    return "";
  }
  return sliceCommentSpan(parsed.commentText, tag.payloadSpan, { offset: parsed.offset });
}
function getConstraintTargetPath(path2) {
  return path2 === void 0 ? null : path2.segments.join(".");
}
function provenanceForTag(sourceFile, tag) {
  const lineAndCharacter = sourceFile.getLineAndCharacterOfPosition(tag.fullSpan.start);
  return {
    surface: "tsdoc",
    file: sourceFile.fileName,
    line: lineAndCharacter.line + 1,
    column: lineAndCharacter.character,
    length: tag.fullSpan.end - tag.fullSpan.start,
    tagName: `@${tag.normalizedTagName}`
  };
}
function updateLowerBound(current, next) {
  return current === void 0 ? next : Math.max(current, next);
}
function updateUpperBound(current, next) {
  return current === void 0 ? next : Math.min(current, next);
}
function buildNumericConstraintFact(targetPath, accumulator) {
  if (accumulator.minimum === void 0 && accumulator.maximum === void 0 && accumulator.exclusiveMinimum === void 0 && accumulator.exclusiveMaximum === void 0 && accumulator.multipleOf === void 0) {
    return null;
  }
  return {
    kind: "numeric-constraints",
    targetPath,
    ...accumulator.minimum === void 0 ? {} : { minimum: accumulator.minimum },
    ...accumulator.maximum === void 0 ? {} : { maximum: accumulator.maximum },
    ...accumulator.exclusiveMinimum === void 0 ? {} : { exclusiveMinimum: accumulator.exclusiveMinimum },
    ...accumulator.exclusiveMaximum === void 0 ? {} : { exclusiveMaximum: accumulator.exclusiveMaximum },
    ...accumulator.multipleOf === void 0 ? {} : { multipleOf: accumulator.multipleOf }
  };
}
function buildStringConstraintFact(targetPath, accumulator) {
  if (accumulator.minLength === void 0 && accumulator.maxLength === void 0 && accumulator.patterns.length === 0) {
    return null;
  }
  return {
    kind: "string-constraints",
    targetPath,
    ...accumulator.minLength === void 0 ? {} : { minLength: accumulator.minLength },
    ...accumulator.maxLength === void 0 ? {} : { maxLength: accumulator.maxLength },
    patterns: accumulator.patterns
  };
}
function buildArrayConstraintFact(targetPath, accumulator) {
  if (accumulator.minItems === void 0 && accumulator.maxItems === void 0 && accumulator.uniqueItems === void 0) {
    return null;
  }
  return {
    kind: "array-constraints",
    targetPath,
    ...accumulator.minItems === void 0 ? {} : { minItems: accumulator.minItems },
    ...accumulator.maxItems === void 0 ? {} : { maxItems: accumulator.maxItems },
    ...accumulator.uniqueItems === void 0 ? {} : { uniqueItems: accumulator.uniqueItems }
  };
}
function formatDeclarationFactMarkdown(fact) {
  switch (fact.kind) {
    case "description":
      return fact.value;
    case "remarks":
      return `Remarks: ${fact.value}`;
    case "default-value":
      return `Default: \`${JSON.stringify(fact.value)}\``;
    case "example":
      return `Example: \`${fact.value}\``;
    case "deprecated":
      return fact.message === null ? "Deprecated" : `Deprecated: ${fact.message}`;
    case "numeric-constraints": {
      const parts = [];
      if (fact.minimum !== void 0) {
        parts.push(`minimum ${String(fact.minimum)}`);
      }
      if (fact.maximum !== void 0) {
        parts.push(`maximum ${String(fact.maximum)}`);
      }
      if (fact.exclusiveMinimum !== void 0) {
        parts.push(`exclusive minimum ${String(fact.exclusiveMinimum)}`);
      }
      if (fact.exclusiveMaximum !== void 0) {
        parts.push(`exclusive maximum ${String(fact.exclusiveMaximum)}`);
      }
      if (fact.multipleOf !== void 0) {
        parts.push(`multiple of ${String(fact.multipleOf)}`);
      }
      return `${renderTargetLabel(fact.targetPath)}: ${parts.join(", ")}`;
    }
    case "string-constraints": {
      const parts = [];
      if (fact.minLength !== void 0 && fact.maxLength !== void 0) {
        parts.push(`length ${String(fact.minLength)}-${String(fact.maxLength)}`);
      } else if (fact.minLength !== void 0) {
        parts.push(`minimum length ${String(fact.minLength)}`);
      } else if (fact.maxLength !== void 0) {
        parts.push(`maximum length ${String(fact.maxLength)}`);
      }
      if (fact.patterns.length > 0) {
        const renderedPatterns = fact.patterns.map((pattern) => `\`${pattern}\``).join(", ");
        parts.push(
          fact.patterns.length === 1 ? `pattern ${renderedPatterns}` : `patterns ${renderedPatterns}`
        );
      }
      return `${renderTargetLabel(fact.targetPath)}: ${parts.join(", ")}`;
    }
    case "array-constraints": {
      const parts = [];
      if (fact.minItems !== void 0 && fact.maxItems !== void 0) {
        parts.push(`items ${String(fact.minItems)}-${String(fact.maxItems)}`);
      } else if (fact.minItems !== void 0) {
        parts.push(`minimum items ${String(fact.minItems)}`);
      } else if (fact.maxItems !== void 0) {
        parts.push(`maximum items ${String(fact.maxItems)}`);
      }
      if (fact.uniqueItems === true) {
        parts.push("unique items");
      }
      return `${renderTargetLabel(fact.targetPath)}: ${parts.join(", ")}`;
    }
    case "allowed-members":
      return `${renderTargetLabel(fact.targetPath)}: allowed members ${fact.members.map((member) => `\`${String(member)}\``).join(", ")}`;
    case "const":
      return `${renderTargetLabel(fact.targetPath)}: constant \`${JSON.stringify(fact.value)}\``;
    case "custom-constraint":
      return `${renderTargetLabel(fact.targetPath)}: constraint \`${fact.constraintId}\` = \`${JSON.stringify(fact.payload)}\``;
    default: {
      const exhaustive = fact;
      return exhaustive;
    }
  }
}
function buildDeclarationHoverMarkdown(summaryText, resolvedMetadata, facts) {
  const lines = ["**FormSpec Declaration Summary**"];
  if (resolvedMetadata?.displayName !== void 0) {
    lines.push("", `Display name: \`${resolvedMetadata.displayName.value}\``);
  }
  if (resolvedMetadata?.apiName !== void 0) {
    lines.push("", `API name: \`${resolvedMetadata.apiName.value}\``);
  }
  if (resolvedMetadata?.displayNamePlural !== void 0) {
    lines.push("", `Display name (plural): \`${resolvedMetadata.displayNamePlural.value}\``);
  }
  if (resolvedMetadata?.apiNamePlural !== void 0) {
    lines.push("", `API name (plural): \`${resolvedMetadata.apiNamePlural.value}\``);
  }
  if (summaryText !== null) {
    lines.push("", summaryText);
  }
  const factLines = facts.filter((fact) => fact.kind !== "description").map((fact) => `- ${formatDeclarationFactMarkdown(fact)}`);
  if (factLines.length > 0) {
    lines.push("", ...factLines);
  }
  return lines.join("\n");
}
function buildDeclarationSummary(node, parsed, checker, extensionDefinitions) {
  const sourceFile = node.getSourceFile();
  const summaryText = extractCommentSummaryText(parsed.commentText);
  const metadataAnalysis = analyzeMetadataForNodeWithChecker({
    checker,
    node,
    ...extensionDefinitions === void 0 ? {} : { extensions: extensionDefinitions }
  });
  const resolvedMetadata = toSerializedResolvedMetadata(metadataAnalysis?.resolvedMetadata);
  const metadataEntries = toSerializedMetadataEntries(metadataAnalysis?.entries ?? []);
  const constraintRegistry = createConstraintTagRegistry(extensionDefinitions);
  const numericConstraints = /* @__PURE__ */ new Map();
  const stringConstraints = /* @__PURE__ */ new Map();
  const arrayConstraints = /* @__PURE__ */ new Map();
  const blockTagTexts = new Map(
    ["deprecated", "example", "remarks"].map((tagName) => [
      tagName,
      extractCommentBlockTagTexts(parsed.commentText, tagName)
    ])
  );
  const blockTagIndexes = /* @__PURE__ */ new Map();
  const facts = [];
  const takeBlockTagText = (tagName) => {
    const values = blockTagTexts.get(tagName);
    if (values === void 0) {
      return null;
    }
    const index = blockTagIndexes.get(tagName) ?? 0;
    blockTagIndexes.set(tagName, index + 1);
    return values[index] ?? null;
  };
  if (summaryText !== "") {
    facts.push({
      kind: "description",
      value: summaryText
    });
  }
  for (const tag of parsed.tags) {
    const payloadText = getTagPayloadText(parsed, tag);
    const constraint = parseConstraintTagValue(
      tag.normalizedTagName,
      payloadText,
      provenanceForTag(sourceFile, tag),
      constraintRegistry === void 0 ? void 0 : { registry: constraintRegistry }
    );
    if (constraint !== null) {
      const targetPath = getConstraintTargetPath(constraint.path);
      switch (constraint.constraintKind) {
        case "minimum":
        case "exclusiveMinimum":
        case "maximum":
        case "exclusiveMaximum":
        case "multipleOf": {
          const current = numericConstraints.get(targetPath) ?? {};
          switch (constraint.constraintKind) {
            case "minimum":
              current.minimum = updateLowerBound(current.minimum, constraint.value);
              break;
            case "exclusiveMinimum":
              current.exclusiveMinimum = updateLowerBound(current.exclusiveMinimum, constraint.value);
              break;
            case "maximum":
              current.maximum = updateUpperBound(current.maximum, constraint.value);
              break;
            case "exclusiveMaximum":
              current.exclusiveMaximum = updateUpperBound(current.exclusiveMaximum, constraint.value);
              break;
            case "multipleOf":
              current.multipleOf = constraint.value;
              break;
          }
          numericConstraints.set(targetPath, current);
          continue;
        }
        case "minLength":
        case "maxLength":
        case "pattern": {
          const current = stringConstraints.get(targetPath) ?? { patterns: [] };
          switch (constraint.constraintKind) {
            case "minLength":
              current.minLength = updateLowerBound(current.minLength, constraint.value);
              break;
            case "maxLength":
              current.maxLength = updateUpperBound(current.maxLength, constraint.value);
              break;
            case "pattern":
              if (!current.patterns.includes(constraint.pattern)) {
                current.patterns.push(constraint.pattern);
              }
              break;
          }
          stringConstraints.set(targetPath, current);
          continue;
        }
        case "minItems":
        case "maxItems":
        case "uniqueItems": {
          const current = arrayConstraints.get(targetPath) ?? {};
          switch (constraint.constraintKind) {
            case "minItems":
              current.minItems = updateLowerBound(current.minItems, constraint.value);
              break;
            case "maxItems":
              current.maxItems = updateUpperBound(current.maxItems, constraint.value);
              break;
            case "uniqueItems":
              current.uniqueItems = constraint.value;
              break;
          }
          arrayConstraints.set(targetPath, current);
          continue;
        }
        case "allowedMembers":
          facts.push({
            kind: "allowed-members",
            targetPath,
            members: constraint.members
          });
          continue;
        case "const":
          facts.push({
            kind: "const",
            targetPath,
            value: constraint.value
          });
          continue;
        case "custom":
          facts.push({
            kind: "custom-constraint",
            targetPath,
            constraintId: constraint.constraintId,
            compositionRule: constraint.compositionRule,
            payload: constraint.payload
          });
          continue;
        default:
          continue;
      }
    }
    switch (tag.normalizedTagName) {
      case "defaultValue": {
        if (tag.argumentText.trim() === "") {
          break;
        }
        const defaultValue = parseDefaultValueTagValue(
          tag.argumentText,
          provenanceForTag(sourceFile, tag)
        );
        if (defaultValue.annotationKind !== "defaultValue") {
          break;
        }
        facts.push({
          kind: "default-value",
          value: defaultValue.value
        });
        break;
      }
      case "example": {
        const value = (takeBlockTagText("example") ?? tag.argumentText).trim();
        if (value !== "") {
          facts.push({
            kind: "example",
            value
          });
        }
        break;
      }
      case "deprecated": {
        const value = (takeBlockTagText("deprecated") ?? tag.argumentText).trim();
        facts.push({
          kind: "deprecated",
          message: value === "" ? null : value
        });
        break;
      }
      case "remarks": {
        const value = (takeBlockTagText("remarks") ?? tag.argumentText).trim();
        if (value !== "") {
          facts.push({
            kind: "remarks",
            value
          });
        }
        break;
      }
      default:
        break;
    }
  }
  for (const [targetPath, accumulator] of numericConstraints.entries()) {
    const fact = buildNumericConstraintFact(targetPath, accumulator);
    if (fact !== null) {
      facts.push(fact);
    }
  }
  for (const [targetPath, accumulator] of stringConstraints.entries()) {
    const fact = buildStringConstraintFact(targetPath, accumulator);
    if (fact !== null) {
      facts.push(fact);
    }
  }
  for (const [targetPath, accumulator] of arrayConstraints.entries()) {
    const fact = buildArrayConstraintFact(targetPath, accumulator);
    if (fact !== null) {
      facts.push(fact);
    }
  }
  return {
    summaryText: summaryText === "" ? null : summaryText,
    resolvedMetadata,
    metadataEntries,
    facts,
    hoverMarkdown: buildDeclarationHoverMarkdown(
      summaryText === "" ? null : summaryText,
      resolvedMetadata,
      facts
    )
  };
}
function typeToString(type, checker) {
  if (type === void 0) {
    return null;
  }
  return checker.typeToString(type, void 0, ts5.TypeFormatFlags.NoTruncation);
}
function supportingDeclarationsForType(type) {
  if (type === void 0) {
    return [];
  }
  const symbol = type.aliasSymbol ?? type.getSymbol();
  const declarations = symbol?.declarations ?? [];
  return declarations.map(
    (declaration) => declaration.getSourceFile().text.slice(declaration.getFullStart(), declaration.getEnd())
  ).filter((declarationText) => declarationText.trim().length > 0);
}
function renderStandaloneTypeSyntax(type, checker) {
  if (type === void 0) {
    return null;
  }
  const typeNode = checker.typeToTypeNode(type, void 0, SYNTHETIC_TYPE_NODE_BUILDER_FLAGS);
  if (typeNode === void 0) {
    return null;
  }
  const rendered = SYNTHETIC_TYPE_PRINTER.printNode(
    ts5.EmitHint.Unspecified,
    typeNode,
    SYNTHETIC_TYPE_PRINT_SOURCE_FILE
  ).trim();
  return rendered === "" ? null : rendered;
}
function requiresSupportingDeclarationsForStandaloneTypeSyntax(typeText) {
  if (typeText === null) {
    return true;
  }
  const sourceFile = ts5.createSourceFile(
    "/virtual/formspec-standalone-type-analysis.ts",
    `type __FormSpecStandalone = ${typeText};`,
    ts5.ScriptTarget.ES2022,
    false,
    ts5.ScriptKind.TS
  );
  const statement = sourceFile.statements[0];
  if (statement === void 0 || !ts5.isTypeAliasDeclaration(statement)) {
    return true;
  }
  let requiresDeclarations = false;
  const visit = (node) => {
    if (ts5.isTypeReferenceNode(node) || ts5.isExpressionWithTypeArguments(node) || ts5.isImportTypeNode(node) || ts5.isTypeQueryNode(node)) {
      requiresDeclarations = true;
      return;
    }
    ts5.forEachChild(node, visit);
  };
  visit(statement.type);
  return requiresDeclarations;
}
function dedupeSupportingDeclarations(declarations) {
  return [...new Set(declarations)];
}
function getSyntheticTargetForTag(tag) {
  if (tag.target === null) {
    return null;
  }
  switch (tag.target.kind) {
    case "path":
    case "member":
    case "variant":
      return {
        kind: tag.target.kind,
        text: tag.target.rawText
      };
    case "ambiguous":
      return {
        kind: "path",
        text: tag.target.rawText
      };
    default: {
      const exhaustive = tag.target.kind;
      return exhaustive;
    }
  }
}
function getDeclaredSubjectType(node, checker, subjectType) {
  if ((ts5.isPropertyDeclaration(node) || ts5.isPropertySignature(node) || ts5.isParameter(node) || ts5.isVariableDeclaration(node) || ts5.isTypeAliasDeclaration(node)) && node.type !== void 0) {
    return checker.getTypeFromTypeNode(node.type);
  }
  return subjectType;
}
function getArgumentExpression(argumentText, valueLabels, capabilityTargets) {
  const trimmed = argumentText.trim();
  if (trimmed === "") {
    return null;
  }
  if (valueLabels.some((label) => label.includes("number") || label.includes("integer"))) {
    return trimmed;
  }
  if (valueLabels.some((label) => label.includes("boolean"))) {
    return trimmed === "true" || trimmed === "false" ? trimmed : null;
  }
  if (valueLabels.some((label) => label.includes("json"))) {
    try {
      return JSON.stringify(JSON.parse(trimmed));
    } catch {
      return null;
    }
  }
  if (valueLabels.some((label) => label.includes("condition"))) {
    return "undefined as unknown as FormSpecCondition";
  }
  if (capabilityTargets.length > 0 || valueLabels.some((label) => label.includes("string"))) {
    return JSON.stringify(trimmed);
  }
  return JSON.stringify(trimmed);
}
function isNullableType(type) {
  if (type.flags & (ts5.TypeFlags.Null | ts5.TypeFlags.Undefined)) {
    return true;
  }
  if (type.isUnion()) {
    return type.types.some(isNullableType);
  }
  return false;
}
function isIdentifierLikeTagOperand(argumentText) {
  return /^[A-Za-z_$][A-Za-z0-9_$]*$/u.test(argumentText.trim());
}
function diagnosticSeverity(code) {
  switch (code) {
    case "INVALID_TAG_ARGUMENT":
    case "INVALID_TAG_PLACEMENT":
    case "SYNTHETIC_SETUP_FAILURE":
    case "UNSUPPORTED_CUSTOM_TYPE_OVERRIDE":
    case "TYPE_MISMATCH":
    case "UNKNOWN_PATH_TARGET":
    case "INVALID_PATH_TARGET":
    case "NESTED_PATH_TARGET":
    case "MISSING_TARGET_FIELD":
    case "OPTIONAL_TARGET_FIELD":
    case "NULLABLE_TARGET_FIELD":
    case "NON_STRINGLIKE_TARGET_FIELD":
    case "INVALID_TYPE_PARAMETER_REFERENCE":
    case "NON_LOCAL_TYPE_PARAMETER":
    case "DUPLICATE_TAG":
      return "error";
    default:
      return "warning";
  }
}
function diagnosticCategory(code) {
  switch (code) {
    case "INVALID_TAG_ARGUMENT":
      return "value-parsing";
    case "INVALID_TAG_PLACEMENT":
    case "SYNTHETIC_SETUP_FAILURE":
    case "UNSUPPORTED_CUSTOM_TYPE_OVERRIDE":
      return "tag-recognition";
    case "TYPE_MISMATCH":
      return "type-compatibility";
    case "UNKNOWN_PATH_TARGET":
      return "target-resolution";
    case "INVALID_PATH_TARGET":
    case "NESTED_PATH_TARGET":
    case "MISSING_TARGET_FIELD":
    case "OPTIONAL_TARGET_FIELD":
    case "NULLABLE_TARGET_FIELD":
    case "NON_STRINGLIKE_TARGET_FIELD":
      return "target-resolution";
    case "INVALID_TYPE_PARAMETER_REFERENCE":
    case "NON_LOCAL_TYPE_PARAMETER":
      return "tag-recognition";
    case "DUPLICATE_TAG":
      return "constraint-validation";
    case "MISSING_SOURCE_FILE":
      return "infrastructure";
    default:
      return "constraint-validation";
  }
}
function combineCommentSpans(spans) {
  const firstSpan = spans[0];
  if (firstSpan === void 0) {
    return null;
  }
  return {
    start: firstSpan.start,
    end: spans[spans.length - 1]?.end ?? firstSpan.end
  };
}
function createAnalysisDiagnostic(code, message, range, data, relatedLocations = []) {
  return {
    code,
    category: diagnosticCategory(code),
    message,
    range,
    severity: diagnosticSeverity(code),
    relatedLocations,
    data
  };
}
function buildDiscriminatorDiagnostics(node, sourceFile, checker, tag, range) {
  const diagnostics = [];
  const directTargets = new Map(
    getDirectPropertyTargets(node, checker).map((target2) => [target2.name, target2])
  );
  const localTypeParameters = new Set(getDeclarationTypeParameterNames(node));
  const visibleTypeParameters = new Set(getVisibleTypeParameterNames(node));
  const targetText = tag.target?.rawText ?? "";
  const target = tag.target;
  if (target?.kind !== "path") {
    diagnostics.push(
      createAnalysisDiagnostic(
        "INVALID_PATH_TARGET",
        'Tag "@discriminator" requires a direct property path target.',
        range,
        {
          tagName: tag.normalizedTagName,
          targetText
        }
      )
    );
    return diagnostics;
  }
  if (!target.valid || target.path === null) {
    diagnostics.push(
      createAnalysisDiagnostic(
        "INVALID_PATH_TARGET",
        'Tag "@discriminator" has an invalid path target.',
        range,
        {
          tagName: tag.normalizedTagName,
          targetText
        }
      )
    );
    return diagnostics;
  }
  if (target.path.segments.length !== 1) {
    diagnostics.push(
      createAnalysisDiagnostic(
        "NESTED_PATH_TARGET",
        'Tag "@discriminator" only supports a single direct property target in v1.',
        range,
        {
          tagName: tag.normalizedTagName,
          targetText
        }
      )
    );
    return diagnostics;
  }
  const propertyName = target.path.segments[0];
  if (propertyName === void 0) {
    return diagnostics;
  }
  const property = directTargets.get(propertyName);
  if (property === void 0) {
    diagnostics.push(
      createAnalysisDiagnostic(
        "MISSING_TARGET_FIELD",
        `Tag "@discriminator" references unknown target field "${propertyName}".`,
        range,
        {
          tagName: tag.normalizedTagName,
          targetText
        }
      )
    );
    return diagnostics;
  }
  const propertySpan = spanFromPos(
    property.declaration.getStart(sourceFile),
    property.declaration.getEnd()
  );
  if (property.optional) {
    diagnostics.push(
      createAnalysisDiagnostic(
        "OPTIONAL_TARGET_FIELD",
        `Tag "@discriminator" target field "${propertyName}" must be required.`,
        range,
        {
          tagName: tag.normalizedTagName,
          targetText
        },
        [
          {
            filePath: sourceFile.fileName,
            range: propertySpan,
            message: "Target field declaration"
          }
        ]
      )
    );
  }
  if (isNullableType(property.type)) {
    diagnostics.push(
      createAnalysisDiagnostic(
        "NULLABLE_TARGET_FIELD",
        `Tag "@discriminator" target field "${propertyName}" must not be nullable.`,
        range,
        {
          tagName: tag.normalizedTagName,
          targetText
        },
        [
          {
            filePath: sourceFile.fileName,
            range: propertySpan,
            message: "Target field declaration"
          }
        ]
      )
    );
  }
  if (!hasTypeSemanticCapability(property.type, checker, "string-like")) {
    diagnostics.push(
      createAnalysisDiagnostic(
        "NON_STRINGLIKE_TARGET_FIELD",
        `Tag "@discriminator" target field "${propertyName}" must be string-like.`,
        range,
        {
          tagName: tag.normalizedTagName,
          targetText,
          targetKind: property.optional ? "optional" : "required"
        },
        [
          {
            filePath: sourceFile.fileName,
            range: propertySpan,
            message: "Target field declaration"
          }
        ]
      )
    );
  }
  const operand = tag.argumentText.trim();
  if (!isIdentifierLikeTagOperand(operand)) {
    diagnostics.push(
      createAnalysisDiagnostic(
        "INVALID_TYPE_PARAMETER_REFERENCE",
        'Tag "@discriminator" requires a single local type parameter name.',
        range,
        {
          tagName: tag.normalizedTagName,
          targetText,
          argumentText: tag.argumentText
        }
      )
    );
    return diagnostics;
  }
  if (localTypeParameters.has(operand)) {
    return diagnostics;
  }
  if (visibleTypeParameters.has(operand)) {
    diagnostics.push(
      createAnalysisDiagnostic(
        "NON_LOCAL_TYPE_PARAMETER",
        `Tag "@discriminator" type parameter "${operand}" must be declared on the same declaration.`,
        range,
        {
          tagName: tag.normalizedTagName,
          targetText,
          argumentText: tag.argumentText
        }
      )
    );
    return diagnostics;
  }
  diagnostics.push(
    createAnalysisDiagnostic(
      "INVALID_TYPE_PARAMETER_REFERENCE",
      `Tag "@discriminator" references unknown type parameter "${operand}".`,
      range,
      {
        tagName: tag.normalizedTagName,
        targetText,
        argumentText: tag.argumentText
      }
    )
  );
  return diagnostics;
}
function buildTagDiagnostics(node, sourceFile, checker, placement, hostType, subjectType, commentTags, semanticOptions, performance2) {
  if (placement === null || subjectType === void 0) {
    return [];
  }
  const declaredSubjectType = getDeclaredSubjectType(node, checker, subjectType);
  const diagnostics = [];
  let discriminatorTagCount = 0;
  const standaloneHostTypeText = optionalMeasure(
    performance2,
    "analysis.renderStandaloneHostType",
    void 0,
    () => renderStandaloneTypeSyntax(hostType, checker)
  );
  const standaloneSubjectTypeText = optionalMeasure(
    performance2,
    "analysis.renderStandaloneSubjectType",
    void 0,
    () => renderStandaloneTypeSyntax(subjectType, checker)
  );
  const hostTypeNeedsDeclarations = requiresSupportingDeclarationsForStandaloneTypeSyntax(standaloneHostTypeText);
  const subjectTypeNeedsDeclarations = requiresSupportingDeclarationsForStandaloneTypeSyntax(standaloneSubjectTypeText);
  const hostTypeText = standaloneHostTypeText ?? typeToString(hostType, checker) ?? "unknown";
  const subjectTypeText = standaloneSubjectTypeText ?? typeToString(subjectType, checker) ?? "unknown";
  const supportingDeclarations = dedupeSupportingDeclarations([
    ...hostTypeNeedsDeclarations ? supportingDeclarationsForType(hostType) : [],
    ...subjectTypeNeedsDeclarations ? supportingDeclarationsForType(subjectType) : []
  ]);
  const syntheticApplications = [];
  for (const tag of commentTags) {
    const semantic = getCommentTagSemanticContext(tag, semanticOptions);
    if (semantic.tagDefinition === null) {
      continue;
    }
    if (tag.normalizedTagName === "discriminator") {
      discriminatorTagCount += 1;
      if (discriminatorTagCount > 1) {
        diagnostics.push(
          createAnalysisDiagnostic(
            "DUPLICATE_TAG",
            'Duplicate "@discriminator" tag. Only one discriminator declaration is allowed.',
            tag.fullSpan,
            {
              tagName: tag.normalizedTagName
            }
          )
        );
        continue;
      }
      diagnostics.push(
        ...buildDiscriminatorDiagnostics(node, sourceFile, checker, tag, tag.fullSpan)
      );
      continue;
    }
    const target = getSyntheticTargetForTag(tag);
    const pathTargetResolution = tag.target?.kind === "path" || tag.target?.kind === "ambiguous" ? tag.target.path === null ? null : resolvePathTargetType(declaredSubjectType, checker, tag.target.path.segments) : null;
    const argumentExpression = getArgumentExpression(
      tag.argumentText,
      semantic.valueLabels,
      semantic.compatiblePathTargets
    );
    try {
      const syntheticOptions = {
        tagName: tag.normalizedTagName,
        placement,
        hostType: hostTypeText,
        subjectType: subjectTypeText,
        supportingDeclarations,
        ...target === null ? {} : { target },
        ...argumentExpression === null ? {} : { argumentExpression },
        ...semanticOptions.extensions === void 0 ? {} : { extensions: semanticOptions.extensions }
      };
      lowerTagApplicationToSyntheticCall(syntheticOptions);
      syntheticApplications.push({
        tag,
        target,
        pathTargetResolution,
        options: syntheticOptions
      });
    } catch (error) {
      diagnostics.push(
        createAnalysisDiagnostic(
          "INVALID_TAG_PLACEMENT",
          error instanceof Error ? error.message : String(error),
          tag.fullSpan,
          {
            tagName: tag.normalizedTagName,
            placement,
            ...target === null ? {} : { targetKind: target.kind, targetText: target.text }
          }
        )
      );
    }
  }
  const batchCheck = optionalMeasure(
    performance2,
    "analysis.syntheticCheckBatch",
    {
      tagCount: syntheticApplications.length
    },
    () => checkSyntheticTagApplicationsDetailed({
      applications: syntheticApplications.map((application) => application.options),
      ...performance2 === void 0 ? {} : { performance: performance2 }
    })
  );
  const globalDiagnosticRange = combineCommentSpans(
    syntheticApplications.map((application) => application.tag.fullSpan)
  );
  if (globalDiagnosticRange !== null) {
    for (const diagnostic of batchCheck.globalDiagnostics) {
      const code = diagnostic.kind === "unsupported-custom-type-override" ? "UNSUPPORTED_CUSTOM_TYPE_OVERRIDE" : diagnostic.kind === "synthetic-setup" ? "SYNTHETIC_SETUP_FAILURE" : "TYPE_MISMATCH";
      diagnostics.push(
        createAnalysisDiagnostic(
          code,
          diagnostic.message,
          globalDiagnosticRange,
          {
            placement,
            tagNames: syntheticApplications.map((application) => application.tag.normalizedTagName),
            ...diagnostic.code > 0 ? { typescriptDiagnosticCode: diagnostic.code } : {}
          }
        )
      );
    }
  }
  for (const [index, result] of batchCheck.applicationResults.entries()) {
    const application = syntheticApplications[index];
    if (application === void 0) {
      continue;
    }
    for (const diagnostic of result.diagnostics) {
      const code = application.target !== null && diagnostic.message.includes("not assignable") ? application.target.kind === "path" && application.pathTargetResolution?.kind === "missing-property" ? "UNKNOWN_PATH_TARGET" : "TYPE_MISMATCH" : diagnostic.message.includes("Expected") ? "INVALID_TAG_ARGUMENT" : diagnostic.message.includes("No overload") ? "INVALID_TAG_PLACEMENT" : "TYPE_MISMATCH";
      diagnostics.push(
        createAnalysisDiagnostic(code, diagnostic.message, application.tag.fullSpan, {
          tagName: application.tag.normalizedTagName,
          placement,
          ...diagnostic.code > 0 ? { typescriptDiagnosticCode: diagnostic.code } : {},
          ...application.target === null ? {} : {
            targetKind: application.target.kind,
            targetText: application.target.text
          },
          ...application.pathTargetResolution?.kind === "missing-property" ? { missingPathSegment: application.pathTargetResolution.segment } : {}
        })
      );
    }
  }
  return diagnostics;
}
function deserializeSnapshotTagsForDiagnostics(snapshot) {
  return snapshot.tags.map((tag) => ({
    rawTagName: tag.rawTagName,
    normalizedTagName: tag.normalizedTagName,
    recognized: tag.recognized,
    fullSpan: tag.fullSpan,
    tagNameSpan: tag.tagNameSpan,
    payloadSpan: tag.payloadSpan,
    colonSpan: tag.target?.colonSpan ?? null,
    target: tag.target === null ? null : {
      rawText: tag.target.rawText,
      valid: tag.target.valid,
      kind: tag.target.kind,
      fullSpan: tag.target.fullSpan,
      colonSpan: tag.target.colonSpan,
      span: tag.target.span,
      path: extractPathTarget(`:${tag.target.rawText}`)?.path ?? null
    },
    argumentSpan: tag.argumentSpan,
    argumentText: tag.argumentText
  }));
}
function buildCommentSnapshot(node, sourceFile, checker, extensions, extensionDefinitions, performance2) {
  return optionalMeasure(
    performance2,
    "analysis.buildCommentSnapshot",
    {
      nodeKind: ts5.SyntaxKind[node.kind]
    },
    () => {
      const docComment = getLastLeadingDocCommentRange(node, sourceFile);
      if (docComment === null) {
        return null;
      }
      const commentText = sourceFile.text.slice(docComment.pos, docComment.end);
      const parsed = parseCommentBlock(commentText, {
        offset: docComment.pos,
        ...extensions === void 0 ? {} : { extensions }
      });
      const declarationSummary = buildDeclarationSummary(
        node,
        parsed,
        checker,
        extensionDefinitions
      );
      if (parsed.tags.length === 0 && declarationSummary.summaryText === null) {
        return null;
      }
      const placement = resolveDeclarationPlacement(node);
      const subjectType = getSubjectType(node, checker);
      const hostType = getHostType(node, checker);
      const semanticOptions = {
        checker,
        ...subjectType === void 0 ? {} : { subjectType },
        ...placement === null ? {} : { placement },
        ...extensions === void 0 ? {} : { extensions }
      };
      const tags = parsed.tags.map(
        (tag) => serializeParsedCommentTag(tag, getCommentTagSemanticContext(tag, semanticOptions))
      );
      return {
        commentSpan: spanFromPos(docComment.pos, docComment.end),
        declarationSpan: spanFromPos(node.getStart(sourceFile), node.getEnd()),
        placement,
        subjectType: typeToString(subjectType, checker),
        hostType: typeToString(hostType, checker),
        declarationSummary,
        tags
      };
    }
  );
}
function buildFormSpecAnalysisFileSnapshot(sourceFile, options) {
  const startedAt = getFormSpecPerformanceNow();
  const comments = [];
  const diagnostics = [];
  const extensions = options.extensions ?? toExtensionTagSources(options.extensionDefinitions);
  const visit = (node) => {
    const placement = resolveDeclarationPlacement(node);
    if (placement !== null) {
      const snapshot2 = buildCommentSnapshot(
        node,
        sourceFile,
        options.checker,
        extensions,
        options.extensionDefinitions,
        options.performance
      );
      if (snapshot2 !== null) {
        comments.push(snapshot2);
        const subjectType = getSubjectType(node, options.checker);
        const hostType = getHostType(node, options.checker);
        diagnostics.push(
          ...optionalMeasure(
            options.performance,
            "analysis.buildTagDiagnostics",
            {
              placement,
              tagCount: snapshot2.tags.length
            },
            () => buildTagDiagnostics(
              node,
              sourceFile,
              options.checker,
              placement,
              hostType,
              subjectType,
              deserializeSnapshotTagsForDiagnostics(snapshot2),
              {
                checker: options.checker,
                ...subjectType === void 0 ? {} : { subjectType },
                placement,
                ...extensions === void 0 ? {} : { extensions }
              },
              options.performance
            )
          )
        );
      }
    }
    ts5.forEachChild(node, visit);
  };
  visit(sourceFile);
  const snapshot = {
    filePath: sourceFile.fileName,
    sourceHash: computeFormSpecTextHash(sourceFile.text),
    generatedAt: (options.now?.() ?? /* @__PURE__ */ new Date()).toISOString(),
    comments,
    diagnostics
  };
  options.performance?.record({
    name: "analysis.buildFileSnapshot",
    durationMs: getFormSpecPerformanceNow() - startedAt,
    detail: {
      filePath: sourceFile.fileName,
      commentCount: comments.length,
      diagnosticCount: diagnostics.length
    }
  });
  return snapshot;
}
export {
  FORMSPEC_ANALYSIS_PROTOCOL_VERSION,
  FORMSPEC_ANALYSIS_SCHEMA_VERSION,
  FORM_SPEC_PLACEMENTS,
  FORM_SPEC_SYNTHETIC_BATCH_CACHE_ENTRIES,
  FORM_SPEC_TARGET_KINDS,
  LruCache,
  NOOP_FORMSPEC_PERFORMANCE_RECORDER,
  analyzeConstraintTargets,
  analyzeMetadataForNode,
  analyzeMetadataForNodeWithChecker,
  analyzeMetadataForSourceFile,
  buildConstraintTargetStates,
  buildFormSpecAnalysisFileSnapshot,
  buildSyntheticHelperPrelude,
  checkSyntheticTagApplication,
  checkSyntheticTagApplications,
  collectCompatiblePathTargets,
  collectReferencedTypeAnnotations,
  collectReferencedTypeConstraints,
  computeFormSpecTextHash,
  createFormSpecPerformanceRecorder,
  dereferenceAnalysisType,
  extractCommentSummaryText,
  extractPathTarget,
  findCommentTagAtOffset,
  findDeclarationForCommentOffset,
  findEnclosingDocComment,
  formatConstraintTargetName,
  formatPathTarget,
  getAllTagDefinitions,
  getCommentCompletionContextAtOffset,
  getCommentCursorTargetAtOffset,
  getCommentHoverInfoAtOffset,
  getCommentTagSemanticContext,
  getConstraintTagDefinitions,
  getFormSpecManifestPath,
  getFormSpecPerformanceNow,
  getFormSpecWorkspaceId,
  getFormSpecWorkspaceRuntimeDirectory,
  getHostType,
  getLastLeadingDocCommentRange,
  getMatchingTagSignatures,
  getSemanticCommentCompletionContextAtOffset,
  getSubjectType,
  getTagCompletionPrefixAtOffset,
  getTagDefinition,
  getTagHoverMarkdown,
  getTypeSemanticCapabilities,
  hasTypeSemanticCapability,
  isFormSpecAnalysisManifest,
  isFormSpecSemanticQuery,
  isFormSpecSemanticResponse,
  lowerTagApplicationToSyntheticCall,
  normalizeFormSpecTagName,
  optionalMeasure,
  parseCommentBlock,
  parseConstraintTagValue,
  parseDefaultValueTagValue,
  parseTagSyntax,
  resolveConstraintTargetState,
  resolveDeclarationPlacement,
  resolvePathTargetType,
  serializeCommentTagSemanticContext,
  serializeCommentTargetSpecifier,
  serializeCompletionContext,
  serializeHoverInfo,
  serializeParsedCommentTag,
  sliceCommentSpan
};
//# sourceMappingURL=internal.js.map